// Version: 3.12
// v3.12: Clearview Agent list at phone width: Rename, Archive and Delete and the last seen time move onto
//        their own row under the device instead of a column beside it, which squeezed the device details
//        into a narrow strip one word per line (useIsMobile). Updated together with ClientManagement v4.05.
// v3.11: Computers tab lists computers added in Action1 since the daily sync (Action1Live.js v1.1), marked
//        "New, patch data at the next sync" in grey rather than Non-compliant; Total counts them. Updated
//        together with ClientManagement v4.04.
// v3.10: "STG Edge Node" in title case on the tag, label and tooltip.
// v3.09: Device Hub network list marks the STG Edge Node's host (edge_node from
//        app.py v4.39) with an "STG Edge Node" tag and names it when it has no label.
// v3.08: Reports page in View as Client hides internal reports. The assessment list
//        showed internal assessments to an admin previewing as the client (the API
//        returns them to admins), and the report count included internal IT reports.
//        Asks the API with ?as_client=1 and also filters on the page.
// v3.07: Compliance Hub opens on the tab named in ?tab= (e.g. ?tab=phishing), so the
//        client dashboard's Phishing simulation card lands on its results.
// v3.06: Computers tab shows live Action1 status and last seen (Action1Live.js v1.0, app.py v4.36), with where the status
//        came from; footer says the patch data is from the daily sync (updated together with ClientManagement v3.94).
// v3.05: Report dates show UK time with GMT, the same as Client Management (updated together).
// v3.04: Number tiles use the shared .stat-card; inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v3.03: "Open in CISO Portal" link uses the shared button classes.
// v3.02: Status labels in sentence case ("Not assessed", "In progress", "Non-compliant", "No data"), like the reports.
// v3.01: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v3.00: Reports page sections share the same actions (ReportSend.js):
//        Generate and Email to client, with a confirm box showing the report
//        and the stored address. Replaces Generate Report Now, Email Report
//        and Run assessment here.
// v2.99: removed an eslint-disable comment naming react-hooks/exhaustive-deps,
//        a rule this build doesn't register (the build failed on it).
// v2.98: Reports page has a Website Analytics Reports section (app.py v3.96
//        saves a copy of each one sent): View and Download for everyone,
//        Delete for STG admins. Kept in step with ClientManagement.js 3.83.
// v2.97: Device Hub network list shows known vulnerabilities on edge-discovered
//        devices for STG admins (CV-29): CVE badge, red dot on affected
//        services and an expandable CVE list (NetworkCves.js).
// v2.96: Agreed report names on the Reports and Schedules pages: IT and Cyber
//        Security Reports and External Cyber Security Assessments (kept in step
//        with ClientManagement.js 3.81).
// v2.95: Reports Hub shows a before/after grade trend for external assessments
//        (B2) from GET /clients/<slug>/assessment-history — first vs latest grade,
//        direction, score delta, and a mini grade timeline. Backend app.py v3.68.
// v2.94: removed an eslint-disable directive naming a rule not registered in
//        the CRA config (react-hooks/exhaustive-deps), which failed the build.
// v2.93: Admins can delete an assessment report (and its internal pair) from
//        the client Assessments section.
// v2.92: Reports Hub shows an Assessments section (external security
//        assessments from clients/<slug>/assessment/reports). Everyone can
//        view/download the client-facing report; admins also get Run
//        assessment (re-scan this client) and Refresh. Internal assessment
//        reports are admin-only, matching the monthly reports.
// v2.91: Discovered Devices subnet card now shows one row per scanned subnet
//        (SiteSubnetsCard): CIDR, device count, site name, public IP and a
//        per-site Updated time from summary.scanners (app.py v3.52). Fixes
//        multi-site agent clients showing a single subnet line. Stats row now
//        stretches so the tiles match the card height.
// v2.90: Network Labels modal — Discovered Subnets section; fetches
//        /api/clients/{slug}/network/discovered_subnets alongside labels;
//        edge subnets auto-merged into editSites on openManage; agent subnets
//        shown with device count; "+ Add as Site" / "✓ In Sites" per row.
// v2.89: Per-host source badge (Edge/Agent) in NetworkSubTab; merged data support.
// v2.85: Device Hub map — switched from CARTO (now requires API key) to OSM
//        tiles with CSS invert filter to maintain dark appearance.
// v2.83: Removed the dead Clearscope assessment subsystem from ReportsHub
//        and the assessment-in-progress banner — Re-assess button/form
//        (posted to /api/assessments, which fires the missing
//        run_assessment.sh), Grade History chart + log-summary panel
//        (read from /api/clients/<slug>/history, which globs for
//        *_assessment.html and has always returned an empty array for
//        every Clearview client), and the activeScan banner (polled
//        /api/assessments/<slug>/status, now permanently orphaned since
//        the only UI path that could start one is gone). None of it has
//        ever rendered anything real. Also dropped GradeChart, TOOL_LABELS,
//        GRADE_ORDER, gradeToY, module-level gradeColor(), and useNavigate
//        import — all exclusively used by the removed code. ReportsHub no
//        longer takes a history prop.
import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { BulkImportModal } from './AssetInventory';
import AssetHub from './AssetHub';
import { authFetch, useAuth } from './auth';
import { ReportActions } from './ReportSend';
import ComplianceTile from './ComplianceTile';
import PhishingSection from './PhishingSection';
import Tickets from './Tickets';
import EmailSecurityDetail from './EmailSecurity';
import CyberSecurityHub from './CyberSecurityHub';
import DigitalHub from './DigitalHub';
import ProductivityHub from './ProductivityHub';
import Schedules from './Schedules';
import Productivity from './Productivity';
import { HostCveBadge, HostCveList, ServiceCveDot } from './NetworkCves';
import { useAction1Live, Action1StatusSource, A1_STATUS_COLOR, fmtA1Time, a1Compliance } from './Action1Live';
import { useIsMobile } from './salesShared';
import './ClientDetail.css';

function GradeBadge({ grade }) {
  const letter = grade ? grade[0] : '?';
  const cls = ['A','B','C','D','E','F'].includes(letter) ? `grade-${letter}` : 'grade-unknown';
  return <span className={`grade-badge ${cls}`}>{grade || '?'}</span>;
}

function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', timeZone:'Europe/London' }) + ' GMT';
}
function formatSize(bytes) {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1048576) return `${(bytes/1024).toFixed(0)}KB`;
  return `${(bytes/1048576).toFixed(1)}MB`;
}

// ── Network sub-tab utilities ─────────────────────────────────────────────────

function ipInSubnet(ip, subnet) {
  try {
    const [net, bits] = subnet.split('/');
    if (!bits) return ip === net;
    const mask   = ~((1 << (32 - parseInt(bits))) - 1) >>> 0;
    const ipInt  = ip.split('.').reduce((a,b) => (a << 8) | parseInt(b), 0) >>> 0;
    const netInt = net.split('.').reduce((a,b) => (a << 8) | parseInt(b), 0) >>> 0;
    return (ipInt & mask) === (netInt & mask);
  } catch { return false; }
}
function siteForIp(ip, sites) {
  for (const [subnet, v] of Object.entries(sites || {})) {
    if (ipInSubnet(ip, subnet)) {
      return typeof v === 'string' ? v : (v?.label || subnet);
    }
  }
  return null;
}
function siteDataForIp(ip, sites) {
  for (const [subnet, v] of Object.entries(sites || {})) {
    if (ipInSubnet(ip, subnet)) {
      return typeof v === 'string' ? {label: v, public_ip: null, subnet} : {...v, subnet};
    }
  }
  return null;
}
// ── Site subnets card ─────────────────────────────────────────────────────────
// One row per scanned subnet: CIDR, device count, site name, public IP and the
// last scan time for that site. Multi-site agent clients scan each site
// separately (app.py v3.51+), so each row carries its own Updated time from
// summary.scanners. Falls back to summary.subnet (comma-separated) for edge data.
function SiteSubnetsCard({ summary, hosts, sites, isAdmin }) {
  const scanners = summary?.scanners || [];
  const fromSummary = String(summary?.subnet || '').split(',').map(s => s.trim()).filter(Boolean);
  const subnets = [...new Set([...fromSummary, ...scanners.map(s => s.subnet).filter(Boolean)])];
  if (!subnets.length) return null;
  const fmt = iso => new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT';
  return (
    <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'12px 18px',minWidth:120,maxWidth:'100%',boxSizing:'border-box',display:'flex',flexDirection:'column',gap:10}}>
      {subnets.map(subnet => {
        const base = subnet.split('/')[0];
        const v = (sites || {})[subnet] ?? Object.entries(sites || {}).find(([k]) => ipInSubnet(base, k))?.[1];
        const siteLabel = typeof v === 'string' ? v : (v?.label || '');
        const pub = v && typeof v === 'object' ? v.public_ip : null;
        const auto = v && typeof v === 'object' && ['edge', 'agent'].includes(v.public_ip_source);
        const count = (hosts || []).filter(h => ipInSubnet(h.ip, subnet)).length;
        const sc = scanners.find(s => s.subnet === subnet);
        return (
          <div key={subnet}>
            <div style={{display:'flex',alignItems:'baseline',gap:8,flexWrap:'wrap'}}>
              <span style={{fontSize:14,fontWeight:700,fontFamily:'var(--font-mono)',color:'var(--text-muted)'}}>{subnet}</span>
              <span style={{fontSize:10,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>{count} device{count === 1 ? '' : 's'}</span>
            </div>
            {siteLabel
              ? <div style={{fontSize:11,color:'var(--accent)',fontWeight:600,marginTop:2}}>{siteLabel}{pub ? ` · ${auto ? '↻ ' : ''}${pub}` : ''}</div>
              : <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.1em',fontWeight:600,marginTop:2}}>Subnet</div>}
            {sc?.scanned_at && <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>Updated {fmt(sc.scanned_at)}{isAdmin && sc.hostname ? ` · ${sc.hostname}` : ''}</div>}
          </div>
        );
      })}
    </div>
  );
}

const SERVICE_COLORS = {
  'Web':'#00d4ff','Secure Web':'#00d4ff','Remote Access':'#ff2d55','Remote Desktop':'#ff2d55',
  'File Sharing':'#e6b800','File Transfer':'#e6b800','Secure File Transfer':'#e6b800',
  'Email':'#30d158','Secure Email':'#30d158','DNS':'#86868b','Network Services':'#86868b',
  'Network Management':'#86868b','Network Discovery':'#86868b','Printing':'#86868b',
  'Windows Services':'#86868b','Database':'#ff9f0a','Directory Services':'#bf5af2',
  'Authentication':'#bf5af2','Network Service':'#86868b',
};

// ── Report Device modal ───────────────────────────────────────────────────────

function ReportDeviceModal({ host, slug, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState('');

  const REASONS = [
    { value: 'unrecognised', label: "I don't recognise this device" },
    { value: 'unexpected',   label: "This device shouldn't be on the network" },
    { value: 'info',         label: "I'd like more information about this device" },
    { value: 'access',       label: "I'm concerned about what this device can access" },
    { value: 'other',        label: "Other" },
  ];

  const deviceId = host.label || host.hostname || host.action1_name || host.ip;
  const services = (host.services||[]).map(s=>s.label).filter((v,i,a)=>a.indexOf(v)===i).join(', ');
  const vendor   = host.vendor && host.vendor !== '(Unknown)' && host.vendor !== 'Unknown' ? host.vendor : '';

  const deviceBlock = [
    `Device details:`,
    `- IP Address: ${host.ip}`,
    host.mac ? `- MAC Address: ${host.mac}` : null,
    vendor    ? `- Manufacturer: ${vendor}`  : null,
    (host.hostname||host.action1_name) ? `- Hostname: ${host.hostname||host.action1_name}` : null,
    services  ? `- Services: ${services}`    : null,
  ].filter(l=>l!==null).join('\n');

  const DESCRIPTIONS = {
    unrecognised: `I've noticed a device on the network that I don't recognise.\n\n${deviceBlock}\n\nCould you please identify this device and confirm whether it should be there?`,
    unexpected:   `There is a device connected to the network that I believe should not be there. Please could you review this and advise.\n\n${deviceBlock}`,
    info:         `I'd like to find out more about the following device on our network.\n\n${deviceBlock}\n\nAny information you can provide would be helpful.`,
    access:       `I have a concern about a device on the network and what it may be able to access. Please could you review this.\n\n${deviceBlock}`,
    other:        `I'd like to raise a query about the following network device.\n\n${deviceBlock}`,
  };

  const deviceDesc = host.device_type || 'Device';
  const TITLES = {
    unrecognised: `Unknown ${deviceDesc.toLowerCase()} on network - ${host.ip}`,
    unexpected:   `Unexpected ${deviceDesc.toLowerCase()} on network - ${host.ip}`,
    info:         `${deviceDesc} query - ${host.ip}`,
    access:       `Network access concern - ${host.ip}`,
    other:        `Network device query - ${host.ip}`,
  };

  const [reason, setReason]     = useState('unrecognised');
  const [title, setTitle]       = useState(TITLES['unrecognised']);
  const [desc, setDesc]         = useState(DESCRIPTIONS['unrecognised']);
  const [priority, setPriority] = useState('Medium');

  const handleReasonChange = (val) => {
    setReason(val); setTitle(TITLES[val]); setDesc(DESCRIPTIONS[val]);
  };

  const inp = { width:'100%' };
  const lbl = { fontSize:11, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', display:'block', marginBottom:5 };
  const sel = { width:'100%' };

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      const res = await authFetch('/api/tickets', { method:'POST', body:JSON.stringify({ type:'Incident', priority, title, description:desc, client_slug:slug, source:'network_devices' }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error||'Failed to raise ticket');
      setSubmitted(true);
    } catch(e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.72)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',width:'100%',maxWidth:580,display:'flex',flexDirection:'column',maxHeight:'90vh',overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px',borderBottom:'1px solid var(--border)',flexShrink:0}}>
          <div style={{fontSize:15,fontWeight:700}}>Raise a Ticket - {deviceId}</div>
          <button className="btn btn-quiet btn-icon" onClick={onClose}>×</button>
        </div>
        {submitted ? (
          <div style={{padding:40,textAlign:'center'}}>
            <div style={{fontSize:36,marginBottom:12}}>✓</div>
            <div style={{fontSize:15,fontWeight:600,color:'#30d158',marginBottom:8}}>Ticket raised successfully</div>
            <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:24}}>Our team will be in touch shortly.</div>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <div style={{padding:20,display:'flex',flexDirection:'column',gap:14,overflowY:'auto'}}>
            <div style={{background:'var(--card-bg)',border:'1px solid var(--border)',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>
              {host.ip}{host.mac?` · ${host.mac}`:''}
            </div>
            <div><label style={lbl}>Reason</label><select className="input-sm" value={reason} onChange={e=>handleReasonChange(e.target.value)} style={sel}>{REASONS.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}</select></div>
            <div><label style={lbl}>Priority</label><select className="input-sm" value={priority} onChange={e=>setPriority(e.target.value)} style={sel}>{['Low','Medium','High','Critical'].map(p=><option key={p}>{p}</option>)}</select></div>
            <div><label style={lbl}>Title</label><input className="input-sm" value={title} onChange={e=>setTitle(e.target.value)} style={inp}/></div>
            <div><label style={lbl}>Additional details</label><textarea className="input-sm" value={desc} onChange={e=>setDesc(e.target.value)} rows={8} style={{ ...inp, resize:'vertical' }}/></div>
            {error&&<div className="error-msg">{error}</div>}
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={submit} disabled={submitting}>{submitting?<span className="spinner"/>:'Raise Ticket'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Network sub-tab ───────────────────────────────────────────────────────────

function NetworkSubTab({ slug, isAdmin }) {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState('');
  const [labels, setLabels]           = useState({ sites:{}, devices:{} });
  const [showManage, setShowManage]   = useState(false);
  const [saving, setSaving]           = useState(false);
  const [saveMsg, setSaveMsg]         = useState('');
  const [editSites, setEditSites]     = useState([]);
  const [editDevices, setEditDevices] = useState([]);
  const [discovered, setDiscovered]   = useState([]);
  const [reportDevice, setReportDevice] = useState(null);
  const [sortCol, setSortCol]         = useState('ip');
  const [sortDir, setSortDir]         = useState('asc');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showNewOnly, setShowNewOnly] = useState(false);

  const handleSort = (col) => {
    if (sortCol === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortCol(col); setSortDir('asc'); }
  };

  const sortHosts = (hostList) => {
    return [...hostList].sort((a, b) => {
      let av, bv;
      if (sortCol === 'ip') {
        av = a.ip.split('.').map(n => parseInt(n).toString().padStart(3,'0')).join('.');
        bv = b.ip.split('.').map(n => parseInt(n).toString().padStart(3,'0')).join('.');
      } else if (sortCol === 'device') {
        av = (labels.devices?.[a.mac]?.label || a.device_type || '').toLowerCase();
        bv = (labels.devices?.[b.mac]?.label || b.device_type || '').toLowerCase();
      } else if (sortCol === 'hostname') {
        av = (a.hostname || a.action1_name || '').toLowerCase();
        bv = (b.hostname || b.action1_name || '').toLowerCase();
      } else { av = ''; bv = ''; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  useEffect(() => {
    authFetch(`/api/clients/${slug}/network`).then(r=>r.json()).then(d=>{setData(d);setLoading(false);}).catch(()=>setLoading(false));
    authFetch(`/api/clients/${slug}/network/labels`).then(r=>r.json()).then(d=>setLabels(d||{sites:{},devices:{}})).catch(()=>{});
    authFetch(`/api/clients/${slug}/network/discovered_subnets`).then(r=>r.json()).then(d=>setDiscovered(d.subnets||[])).catch(()=>{});
  }, [slug]);

  const openManage = () => {
    const sites = Object.entries(labels.sites||{}).map(([subnet,v])=>({
      subnet,
      name: typeof v === 'string' ? v : (v.label || subnet),
      public_ip: typeof v === 'object' ? (v.public_ip || '') : '',
      public_ip_source: typeof v === 'object' ? (v.public_ip_source || 'manual') : 'manual',
      edge_id: typeof v === 'object' ? (v.edge_id || '') : '',
    }));
    // Auto-merge edge subnets not already in saved sites
    const existing = new Set(sites.map(s => s.subnet));
    (discovered||[]).filter(s => s.source==='edge' && !existing.has(s.subnet)).forEach(s => {
      sites.push({ subnet: s.subnet, name: s.edge_label || '', public_ip: '', public_ip_source: 'edge', edge_id: s.edge_id || '' });
    });
    setEditSites(sites);
    const hosts=data?.hosts||[], devMap={};
    Object.entries(labels.devices||{}).forEach(([mac,dev])=>{devMap[mac]={...dev,mac};});
    hosts.forEach(h=>{if(h.mac&&!devMap[h.mac])devMap[h.mac]={mac:h.mac,label:'',ip:h.ip,vendor:h.vendor||'',notes:''};});
    setEditDevices(Object.values(devMap).sort((a,b)=>a.ip?.localeCompare(b.ip)||0));
    setShowManage(true); setSaveMsg('');
  };

  const saveLabels = async () => {
    setSaving(true); setSaveMsg('');
    const sites={}, devices={};
    editSites.forEach(({subnet,name,public_ip,public_ip_source,edge_id})=>{
      if(subnet.trim()&&name.trim()) sites[subnet.trim()]={
        label: name.trim(),
        public_ip: (public_ip||'').trim() || null,
        public_ip_source: public_ip_source || 'manual',
        edge_id: edge_id || null,
      };
    });
    editDevices.forEach(({mac,label,ip,vendor,notes})=>{if(mac)devices[mac.toLowerCase()]={label:label||'',ip:ip||'',vendor:vendor||'',notes:notes||''};});
    try {
      const res=await authFetch(`/api/clients/${slug}/network/labels`,{method:'POST',body:JSON.stringify({sites,devices})});
      const d=await res.json(); if(!res.ok)throw new Error(d.error||'Save failed');
      setLabels({sites,devices}); setSaveMsg('Saved');
      setTimeout(()=>{setSaveMsg('');setShowManage(false);},1200);
    } catch(e){setSaveMsg('Error: '+e.message);}
    finally{setSaving(false);}
  };

  const hosts=data?.hosts||[], summary=data?.summary||{};
  const newCount=hosts.filter(h=>h.is_new).length;
  const groupedBySite={}, ungrouped=[];
  hosts.forEach(host=>{
    const site=siteForIp(host.ip,labels.sites);
    if(site){(groupedBySite[site]=groupedBySite[site]||[]).push(host);}else ungrouped.push(host);
  });

  if (loading) return <div style={{padding:32,textAlign:'center'}}><span className="spinner"/></div>;
  if (!data?.available) return <div className="cd-empty">No network scan data yet. Scans run automatically when a Clearview Edge node or an agent-based primary scanner is active on the client network.</div>;

  const renderTable = (hostList, title) => {
    const _q=filter.toLowerCase();
    const filtered=hostList.filter(h=>
      (!showNewOnly || h.is_new) &&
      (!filter||
        h.ip.includes(filter)||
        (h.hostname||'').toLowerCase().includes(_q)||
        (h.label||'').toLowerCase().includes(_q)||
        ((h.mac&&labels.devices?.[h.mac]?.label)||'').toLowerCase().includes(_q)||
        (h.vendor||'').toLowerCase().includes(_q)||
        (h.device_type||'').toLowerCase().includes(_q)||
        (h.services||[]).some(s=>(s.label||'').toLowerCase().includes(_q)))
    );
    if(!filtered.length)return null;
    return (
      <div key={title||'all'} style={{marginBottom:title?16:0}}>
        {title&&<div style={{fontSize:11,fontWeight:600,letterSpacing:'0.1em',textTransform:'uppercase',color:'var(--text-muted)',marginBottom:6,fontFamily:'var(--font-mono)'}}>{title}</div>}
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {sortHosts(filtered).map(host=>{
            const macKey=(host.mac||'').toLowerCase();
            const devLabel=labels.devices?.[macKey]?.label||'';
            const services=host.services||[];
            const unique=isAdmin?services:services.filter((s,i,a)=>a.findIndex(x=>x.label===s.label)===i);
            const hostname=host.hostname||host.action1_name||'';
            return (
              <div key={host.ip} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'12px 16px'}}>
                {/* Row 1: label/type + IP + badges */}
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8,marginBottom:6}}>
                  <div style={{minWidth:0}}>
                    {devLabel
                      ?<div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{devLabel}</div>
                      :host.edge_node
                        ?<div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>STG Edge Node (NAS)</div>
                      :host.device_type
                        ?<div style={{fontSize:13,fontWeight:500,color:'var(--text)'}}>{host.device_type}</div>
                        :<div style={{fontSize:12,color:'var(--text-muted)',fontStyle:'italic'}}>Unlabelled</div>}
                    <div style={{fontSize:11,color:'var(--text-muted)',fontFamily:'var(--font-mono)',marginTop:2}}>{host.ip}</div>
                    {hostname&&<div style={{fontSize:11,color:'var(--text-muted)',marginTop:1}}>{hostname}</div>}
                    {isAdmin&&host.mac&&<div style={{fontSize:10,color:'var(--text-muted)',fontFamily:'var(--font-mono)',marginTop:1}}>{host.mac}</div>}
                    {isAdmin&&host.snmp_location&&<div style={{fontSize:10,color:'var(--text-muted)',marginTop:1}}>Location: {host.snmp_location}</div>}
                    {isAdmin&&host.os&&<div style={{fontSize:10,color:'var(--text-muted)',marginTop:1}}>{host.os}</div>}
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4,flexShrink:0}}>
                    {host.edge_node&&<span className="tag tag-blue" title={isAdmin?`${host.edge_node.label}: the Edge Node${host.edge_node.role==='nas'?"'s NAS":''}`:'The STG Edge Node at this site. It monitors your network, compliance and backups.'}>STG Edge Node</span>}
                    {host.is_new&&<span style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:3,background:'#e6b80020',border:'1px solid #e6b80060',color:'#e6b800'}}>NEW</span>}
                    {host.snmp_risk?.length>0&&<span style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:3,background:'#ff2d5520',border:'1px solid #ff2d5560',color:'#ff2d55'}}>! SNMP</span>}
                    {isAdmin&&<HostCveBadge host={host}/>}
                    {host.source==='agent'&&<span style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:3,background:'rgba(48,209,88,0.12)',border:'1px solid rgba(48,209,88,0.4)',color:'#30d158'}}>Agent</span>}
                    {host.source==='edge'&&<span style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:3,background:'rgba(0,212,255,0.10)',border:'1px solid rgba(0,212,255,0.35)',color:'var(--accent)'}}>Edge</span>}
                  </div>
                </div>
                {/* Row 2: service tags */}
                {unique.length>0&&(
                  <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:8}}>
                    {unique.map((s,i)=>{
                      const color=SERVICE_COLORS[s.label]||'#86868b';
                      return <span key={i} title={isAdmin&&s.port?`Port ${s.port} · ${s.service||s.label}`:''} style={{padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:600,background:`${color}18`,border:`1px solid ${color}40`,color,fontFamily:'var(--font-mono)',whiteSpace:'nowrap',cursor:isAdmin&&s.port?'help':'default'}}>
                        {isAdmin&&s.port&&<span style={{opacity:0.6,fontSize:10,marginRight:3}}>{s.port}</span>}{s.label}{isAdmin&&<ServiceCveDot service={s}/>}
                      </span>;
                    })}
                  </div>
                )}
                {isAdmin&&<HostCveList host={host}/>}
                {/* Row 3: raise ticket button for clients - right aligned */}
                {!isAdmin&&(
                  <div style={{display:'flex',justifyContent:'flex-end'}}>
                    <button className="btn btn-secondary btn-sm" onClick={()=>setReportDevice(host)}>
                      ⚑ Raise Ticket
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{marginTop:6,fontSize:11,color:'var(--text-muted)'}}>
          {filtered.length} of {hostList.length} device{hostList.length!==1?'s':''}
          {isAdmin&&<span style={{marginLeft:8,fontStyle:'italic'}}>· Hover service tags to see port detail</span>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,marginBottom:16,flexWrap:'wrap'}}>
        <div style={{display:'flex',gap:12,flexWrap:'wrap',alignItems:'stretch'}}>
          {[{label:'Total Devices',val:summary.total_hosts??0,color:'var(--accent)',clickable:false},{label:'New Since Last',val:newCount,color:newCount>0?'#e6b800':'var(--text-muted)',clickable:newCount>0}].map(({label,val,color,clickable})=>{
            const isActive = clickable && showNewOnly;
            return (
              <div key={label}
                onClick={clickable ? () => setShowNewOnly(v => !v) : undefined}
                style={{
                  background: isActive ? '#e6b80012' : 'var(--surface)',
                  border: isActive ? '1px solid #e6b80060' : '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)', padding: '12px 18px', minWidth: 100,
                  cursor: clickable ? 'pointer' : 'default',
                  transition: 'border 0.15s, background 0.15s',
                  userSelect: 'none',
                }}>
                <div style={{fontSize:22,fontWeight:800,color,fontFamily:'var(--font-mono)',lineHeight:1,marginBottom:4}}>{val}</div>
                <div style={{fontSize:10,color:isActive?'#e6b800':'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.1em',fontWeight:600}}>
                  {isActive ? 'New Only · Clear' : label}
                </div>
              </div>
            );
          })}
          <SiteSubnetsCard summary={summary} hosts={hosts} sites={labels.sites} isAdmin={isAdmin}/>
          {summary.scanned_at&&<div style={{fontSize:11,color:'var(--text-muted)',paddingBottom:4,alignSelf:'flex-end'}}>Updated {new Date(summary.scanned_at).toLocaleString('en-GB',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})} GMT</div>}
          {data?.source&&<div style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:4,
            background:data.source==='edge'?'rgba(0,212,255,0.10)':'rgba(48,209,88,0.10)',
            border:data.source==='edge'?'1px solid rgba(0,212,255,0.30)':'1px solid rgba(48,209,88,0.30)',
            color:data.source==='edge'?'var(--accent)':'#30d158',
            fontFamily:'var(--font-mono)',letterSpacing:'0.06em',alignSelf:'flex-end',paddingBottom:4}}>
            {data.source==='edge'?'Edge':'Agent'}
          </div>}
        </div>
        {isAdmin&&(
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-secondary btn-sm" onClick={()=>setShowBulkImport(true)}>⊕ Import to Assets Hub</button>
            <button className="btn btn-secondary btn-sm" onClick={openManage}>⚙ Manage Labels</button>
          </div>
        )}
      </div>

      <input className="input-sm" type="text" placeholder="Filter by IP, hostname or label…" value={filter} onChange={e=>setFilter(e.target.value)}
        style={{ width:'100%', marginBottom:12 }}/>

      {Object.keys(labels.sites||{}).length>0
        ?<>{Object.entries(groupedBySite).map(([site,hosts])=>renderTable(hosts,site))}{ungrouped.length>0&&renderTable(ungrouped,'Other')}</>
        :renderTable(hosts,null)}

      {reportDevice&&<ReportDeviceModal host={reportDevice} slug={slug} onClose={()=>setReportDevice(null)}/>}

      {showBulkImport && isAdmin && (
        <BulkImportModal
          slug={slug}
          defaultTab="discovered"
          onClose={()=>setShowBulkImport(false)}
          onImported={()=>setShowBulkImport(false)}
        />
      )}

      {showManage&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.72)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',width:'100%',maxWidth:700,maxHeight:'85vh',display:'flex',flexDirection:'column',overflow:'hidden'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px',borderBottom:'1px solid var(--border)'}}>
              <div style={{fontSize:15,fontWeight:700}}>Manage Network Labels</div>
              <button className="btn btn-quiet btn-icon" onClick={()=>setShowManage(false)}>×</button>
            </div>
            <div style={{flex:1,overflowY:'auto',padding:20,display:'flex',flexDirection:'column',gap:24}}>

              {/* ── Discovered Subnets ───────────────────────────────────────── */}
              {(()=>{
                const discBySubnet={};
                (discovered||[]).forEach(d=>{if(!discBySubnet[d.subnet])discBySubnet[d.subnet]=[];discBySubnet[d.subnet].push(d);});
                const discEntries=Object.entries(discBySubnet);
                if(!discEntries.length) return null;
                return (
                  <div>
                    <div style={{fontSize:11,fontWeight:600,letterSpacing:'0.1em',textTransform:'uppercase',color:'var(--text-muted)',marginBottom:6,fontFamily:'var(--font-mono)'}}>Discovered Subnets</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:10}}>Detected from agent check-ins and edge node configurations. Edge node subnets are added to Sites automatically.</div>
                    <div style={{display:'flex',flexDirection:'column',gap:6}}>
                      {discEntries.map(([subnet,sources])=>{
                        const alreadyAdded=editSites.some(s=>s.subnet===subnet);
                        return (
                          <div key={subnet} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderRadius:8,background:'var(--card-bg)',border:'1px solid var(--border)'}}>
                            <div style={{fontFamily:'var(--font-mono)',fontSize:12,flex:'0 0 150px',color:'var(--text)'}}>{subnet}</div>
                            <div style={{display:'flex',gap:6,flex:1,flexWrap:'wrap'}}>
                              {sources.map((s,i)=>(
                                <span key={i} style={{
                                  fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:4,textTransform:'uppercase',letterSpacing:'0.06em',
                                  background:s.source==='edge'?'#00d4ff18':'#30d15818',
                                  color:s.source==='edge'?'#00d4ff':'#30d158',
                                  border:`1px solid ${s.source==='edge'?'#00d4ff40':'#30d15840'}`,
                                }}>
                                  {s.source==='edge'?`Edge · ${s.edge_label||s.edge_id||''}`:`Agent · ${s.device_count} device${s.device_count!==1?'s':''}`}
                                </span>
                              ))}
                            </div>
                            {alreadyAdded?(
                              <span style={{fontSize:11,color:'#30d158',fontWeight:700,whiteSpace:'nowrap'}}>✓ In Sites</span>
                            ):(
                              <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }}
                                onClick={()=>setEditSites(prev=>[...prev,{subnet,name:'',public_ip:'',public_ip_source:sources.find(s=>s.source==='edge')?'edge':'manual',edge_id:sources.find(s=>s.source==='edge')?.edge_id||''}])}
                               >
                                + Add as Site
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* ── Sites ────────────────────────────────────────────────────── */}
              <div>
                <div style={{fontSize:11,fontWeight:600,letterSpacing:'0.1em',textTransform:'uppercase',color:'var(--text-muted)',marginBottom:10,fontFamily:'var(--font-mono)'}}>Sites (Subnets)</div>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  <div style={{display:'flex',gap:8,paddingBottom:4,borderBottom:'1px solid var(--border)'}}>
                    <div style={{flex:'0 0 160px',fontSize:10,color:'var(--text-muted)',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.08em'}}>Subnet</div>
                    <div style={{flex:1,fontSize:10,color:'var(--text-muted)',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.08em'}}>Site Name</div>
                    <div style={{flex:'0 0 140px',fontSize:10,color:'var(--text-muted)',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.08em'}}>Public IP <span style={{color:'var(--accent)',fontWeight:400}}>(auto or manual)</span></div>
                    <div style={{width:18}}/>
                  </div>
                  {editSites.map((s,i)=>(
                    <div key={i} style={{display:'flex',gap:8,alignItems:'center'}}>
                      <input className="input-sm" value={s.subnet} onChange={e=>{const ns=[...editSites];ns[i]={...ns[i],subnet:e.target.value};setEditSites(ns);}} placeholder="192.168.168.0/24" style={{ flex:'0 0 160px' }}/>
                      <input className="input-sm" value={s.name} onChange={e=>{const ns=[...editSites];ns[i]={...ns[i],name:e.target.value};setEditSites(ns);}} placeholder="Main Office" style={{ flex:1 }}/>
                      <input className="input-sm" value={s.public_ip||''} onChange={e=>{const ns=[...editSites];ns[i]={...ns[i],public_ip:e.target.value,public_ip_source:'manual'};setEditSites(ns);}} placeholder="Public IP (optional)" style={{ flex:'0 0 140px' }} title={s.public_ip_source==='edge'?`Auto-detected from Edge ${s.edge_id||''}`:'Manually entered'}/>
                      <button className="btn btn-danger btn-lg" onClick={()=>setEditSites(editSites.filter((_,j)=>j!==i))}>×</button>
                    </div>
                  ))}
                  <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={()=>setEditSites([...editSites,{subnet:'',name:'',public_ip:'',public_ip_source:'manual',edge_id:''}])}>+ Add Site</button>
                </div>
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:600,letterSpacing:'0.1em',textTransform:'uppercase',color:'var(--text-muted)',marginBottom:10,fontFamily:'var(--font-mono)'}}>Devices ({editDevices.length} found)</div>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {editDevices.map((d,i)=>(
                    <div key={d.mac} style={{display:'flex',gap:8,alignItems:'center',padding:'8px 10px',borderRadius:6,background:'var(--card-bg)',border:'1px solid var(--border)'}}>
                      <div style={{flex:'0 0 150px'}}>
                        <div style={{fontSize:10,fontFamily:'var(--font-mono)',color:'var(--text-muted)'}}>{d.mac}</div>
                        <div style={{fontSize:10,color:'var(--text-muted)'}}>{d.ip}{d.vendor?` · ${d.vendor}`:''}</div>
                      </div>
                      <input className="input-sm" value={d.label} onChange={e=>{const nd=[...editDevices];nd[i]={...nd[i],label:e.target.value};setEditDevices(nd);}} placeholder="e.g. Office Router, HP Printer…" style={{ flex:1 }}/>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10,padding:'14px 20px',borderTop:'1px solid var(--border)'}}>
              {saveMsg&&<span style={{fontSize:12,color:saveMsg.startsWith('Error')?'#ff2d55':'#30d158',fontWeight:600}}>{saveMsg}</span>}
              <div style={{marginLeft:'auto',display:'flex',gap:8}}>
                <button className="btn btn-secondary" onClick={()=>setShowManage(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveLabels} disabled={saving}>{saving?'Saving…':'Save Labels'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Managed Computers sub-tab ─────────────────────────────────────────────────

function ComputersSubTab({ slug, isAdmin }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);

  useEffect(() => {
    authFetch(`/api/clients/${slug}/action1`).then(r=>r.json()).then(d=>{setData(d);setLoading(false);}).catch(()=>setLoading(false));
  }, [slug]);

  const { endpoints, live, newCount } = useAction1Live(slug, data?.data?.endpoints);
  const summary=data?.data?.summary||{};
  const filtered=endpoints.filter(ep=>!filter||(ep.name||'').toLowerCase().includes(filter.toLowerCase())||(ep.user||'').toLowerCase().includes(filter.toLowerCase())||(ep.os||'').toLowerCase().includes(filter.toLowerCase())||(ep.ip||'').includes(filter)||(ep.manufacturer||'').toLowerCase().includes(filter.toLowerCase())||(ep.model||'').toLowerCase().includes(filter.toLowerCase()));

  if (loading) return <div style={{padding:32,textAlign:'center'}}><span className="spinner"/></div>;
  if (!data?.available) return <div className="cd-empty">No managed computer data yet.</div>;

  return (
    <div>
      {/* Summary stats */}
      <div className="stat-tiles">{[
          {label:'Total',           val:endpoints.length,          tone:''},
          {label:'Compliant',       val:summary.compliant??0,      tone:'green'},
          {label:'Non-compliant',   val:summary.non_compliant??0,  tone:summary.non_compliant>0?'red':'muted'},
          {label:'Critical updates',val:summary.with_critical??0,  tone:summary.with_critical>0?'red':'muted'},
          {label:'EOL OS',          val:summary.os_eol??0,         tone:summary.os_eol>0?'red':'muted'},
        ].map(({label,val,tone})=>(
          <div key={label} className="stat-card">
            <div className={`stat-value ${tone}`}>{val}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Filter + import */}
      <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center'}}>
        <input className="input-sm" type="text" placeholder="Filter by device, user, OS or IP…" value={filter} onChange={e=>setFilter(e.target.value)}
          style={{ flex:1 }}/>
        {isAdmin && (
          <button className="btn btn-secondary btn-sm" onClick={()=>setShowBulkImport(true)}>⊕ Import to Assets Hub</button>
        )}
      </div>

      <Action1StatusSource live={live} scannedAt={data?.scanned_at} newCount={newCount}/>

      {/* Device cards */}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {filtered.map(ep=>{
          const statusColor = A1_STATUS_COLOR[ep.status]||'#86868b';
          const comp = a1Compliance(ep);
          return (
            <div key={ep.id} style={{background:'var(--surface)',border:'1px solid var(--border)',borderLeft:`3px solid ${comp.color}`,borderRadius:'var(--radius-lg)',padding:'12px 16px'}}>
              {/* Row 1: name + compliant badge */}
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8,marginBottom:6}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:'var(--text)',fontFamily:'var(--font-mono)'}}>{ep.name}</div>
                  {ep.model&&<div style={{fontSize:11,color:'var(--text-muted)',marginTop:1}}>{ep.manufacturer} {ep.model}</div>}
                  {ep.ip&&<div style={{fontSize:11,color:'var(--text-muted)',fontFamily:'var(--font-mono)',marginTop:1}}>{ep.ip}</div>}
                </div>
                <span style={{fontSize:13,fontWeight:700,color:comp.color,flexShrink:0}}>{comp.label}</span>
              </div>
              {/* Row 2: user / OS / status chips */}
              <div style={{display:'flex',flexWrap:'wrap',gap:8,fontSize:11}}>
                {ep.user&&(
                  <span style={{color:'var(--text-muted)'}}>{ep.user}</span>
                )}
                <span style={{color:ep.os_eol?'#ff2d55':'var(--text-muted)'}}>
                  {ep.os}{ep.os_eol?` - EOL ${ep.os_eol_date}`:''}
                </span>
                <span style={{fontWeight:600,color:statusColor}}>{ep.status||'-'}</span>
                {ep.status!=='Connected'&&ep.last_seen&&(
                  <span style={{color:'var(--text-muted)'}}>Last seen {fmtA1Time(ep.last_seen)}</span>
                )}
                {ep.missing_critical>0&&(
                  <span style={{fontWeight:700,color:'#ff2d55'}}>{ep.missing_critical} critical update{ep.missing_critical!==1?'s':''}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{marginTop:8,fontSize:11,color:'var(--text-muted)'}}>
        {filtered.length} of {endpoints.length} device{endpoints.length!==1?'s':''}
        {data?.scanned_at&&<span style={{marginLeft:8}}>· Patch data from the daily sync · Updated {new Date(data.scanned_at).toLocaleString('en-GB',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})} GMT</span>}
      </div>

      {showBulkImport && isAdmin && (
        <BulkImportModal
          slug={slug}
          defaultTab="managed"
          onClose={()=>setShowBulkImport(false)}
          onImported={()=>setShowBulkImport(false)}
        />
      )}
    </div>
  );
}

// ── Hub page wrapper ──────────────────────────────────────────────────────────

function HubPage({ title, subtitle, children }) {
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text)' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

// ── Hub sub-tab bar ───────────────────────────────────────────────────────────

function SubTabBar({ tabs, active, onChange }) {
  return (
    <div style={{ overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
      <div style={{ display: 'flex', gap: 2, minWidth: 'max-content' }}>
        {tabs.map(t => (
          <button className="tab-btn" aria-selected={active === t.id} key={t.id} onClick={() => onChange(t.id)}>{t.label}</button>
        ))}
      </div>
    </div>
  );
}

// ── Cyber Security Hub ────────────────────────────────────────────────────────

function CyberSecurityHubWrapper({ slug }) {
  return (
    <HubPage title="Cyber Security Hub" subtitle="External threats, email security and internet exposure">
      <CyberSecurityHub slug={slug} />
    </HubPage>
  );
}

// ── Digital Hub ───────────────────────────────────────────────────────────────

function DigitalHubWrapper({ slug, isAdmin }) {
  return (
    <HubPage title="Digital Hub" subtitle="Online presence, analytics and website health">
      <DigitalHub slug={slug} isAdmin={isAdmin} />
    </HubPage>
  );
}

// ── Schedules Hub ────────────────────────────────────────────────────────────

function SchedulesHubWrapper({ slug }) {
  return (
    <HubPage title="Schedules" subtitle="Manage when your IT and Cyber Security Report is generated and sent">
      <Schedules hideHeader slug={slug} />
    </HubPage>
  );
}

// ── Device Hub ────────────────────────────────────────────────────────────────

// ── Clearview Agent sub-tab ───────────────────────────────────────────────────

function AgentStatusBadge({ status }) {
  const cfg = {
    online:  { color: '#30d158', label: 'Online' },
    offline: { color: '#ff2d55', label: 'Offline' },
    unknown: { color: '#86868b', label: 'Unknown' },
  }[status] || { color: '#86868b', label: status };
  return (
    <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:4,
      background:`${cfg.color}18`,border:`1px solid ${cfg.color}40`,color:cfg.color,
      whiteSpace:'nowrap',fontFamily:'var(--font-mono)'}}>
      {cfg.label}
    </span>
  );
}


// ── Device enrichment pills ───────────────────────────────────────────────────
function DeviceEnrichmentPills({ dev }) {
  const pills = [];

  // Hardware make/model
  if (dev.hardware?.manufacturer || dev.hardware?.model) {
    const label = [dev.hardware.manufacturer, dev.hardware.model].filter(Boolean).join(' ');
    pills.push(
      <span key="hardware" style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:10,
        padding:'2px 7px',borderRadius:4,background:'var(--surface)',border:'1px solid var(--border)',color:'var(--text-muted)'}}>
         {label}
      </span>
    );
  }

  // Firmware updates pending
  if (dev.firmware_updates?.length) {
    const oldest = dev.firmware_updates.reduce((a, b) => {
      return new Date(a.detected_at) < new Date(b.detected_at) ? a : b;
    });
    const ageDays = oldest.detected_at
      ? Math.floor((Date.now() - new Date(oldest.detected_at)) / 86400000)
      : null;
    const overdue  = ageDays !== null && ageDays >= 14;
    const color    = overdue ? '#ff2d55' : '#e6b800';
    const bg       = overdue ? '#ff2d5518' : '#e6b80018';
    const bdr      = overdue ? '#ff2d5540' : '#e6b80040';
    const ageLabel = ageDays !== null ? ` · ${ageDays}d` : '';
    pills.push(
      <span key="firmware" style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:10,fontWeight:600,
        padding:'2px 7px',borderRadius:4,background:bg,border:`1px solid ${bdr}`,color}}
        title={dev.firmware_updates.map(u => u.title).join('\n')}>
        ⬆ Firmware update pending{ageLabel}{overdue ? ' — overdue' : ''}
      </span>
    );
  }

  // Pending reboot
  if (dev.pending_reboot?.pending) {
    pills.push(
      <span key="reboot" style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:10,fontWeight:600,
        padding:'2px 7px',borderRadius:4,background:'#e6b80018',border:'1px solid #e6b80040',color:'#e6b800'}}>
        ↺ Reboot required{dev.pending_reboot.reason ? ' · '+dev.pending_reboot.reason : ''}
      </span>
    );
  }

  // Logged in user
  if (dev.logged_in_user) {
    pills.push(
      <span key="user" style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:10,
        padding:'2px 7px',borderRadius:4,background:'var(--surface)',border:'1px solid var(--border)',color:'var(--text-muted)'}}>
         {dev.logged_in_user}
      </span>
    );
  }

  // Uptime
  if (dev.uptime_seconds > 0) {
    const days = Math.floor(dev.uptime_seconds / 86400);
    const hrs  = Math.floor((dev.uptime_seconds % 86400) / 3600);
    const label = days > 0 ? `Up ${days}d ${hrs}h` : `Up ${hrs}h`;
    const amber = days >= 7;
    pills.push(
      <span key="uptime" style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:10,
        padding:'2px 7px',borderRadius:4,
        background:amber?'#e6b80018':'var(--surface)',
        border:`1px solid ${amber?'#e6b80040':'var(--border)'}`,
        color:amber?'#e6b800':'var(--text-muted)'}}>
        ⏱ {label}
      </span>
    );
  }

  // Disk space — one pill per drive
  if (dev.disk_space?.length) {
    dev.disk_space.forEach(drive => {
      const pct = drive.used_pct;
      const red   = pct >= 95;
      const amber = pct >= 85 && !red;
      const color = red ? '#ff2d55' : amber ? '#e6b800' : 'var(--text-muted)';
      const bg    = red ? '#ff2d5518' : amber ? '#e6b80018' : 'var(--surface)';
      const bdr   = red ? '#ff2d5540' : amber ? '#e6b80040' : 'var(--border)';
      pills.push(
        <span key={`disk-${drive.letter}`} style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:10,
          padding:'2px 7px',borderRadius:4,background:bg,border:`1px solid ${bdr}`,color}}>
           {drive.letter} {Math.round(pct)}%
        </span>
      );
    });
  }

  // Battery charge
  if (dev.battery) {
    const pct      = dev.battery.percent_remaining;
    const charging = dev.battery.charging;
    const lowBatt  = pct < 20 && !charging;
    const chargeColor = charging ? '#30d158' : lowBatt ? '#ff2d55' : 'var(--text-muted)';
    const chargeBg    = charging ? '#30d15818' : lowBatt ? '#ff2d5518' : 'var(--surface)';
    const chargeBdr   = charging ? '#30d15840' : lowBatt ? '#ff2d5540' : 'var(--border)';
    pills.push(
      <span key="battery-charge" style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:10,
        padding:'2px 7px',borderRadius:4,background:chargeBg,border:`1px solid ${chargeBdr}`,color:chargeColor}}>
        {charging ? '⚡' : ''} {pct}%
      </span>
    );

    // Battery health — always show
    const health      = dev.battery.health_pct;
    if (health > 0) {
      const healthRed   = health < 60;
      const healthAmber = health < 80 && !healthRed;
      const healthGreen = health >= 80;
      const healthColor = healthRed ? '#ff2d55' : healthAmber ? '#e6b800' : '#30d158';
      const healthBg    = healthRed ? '#ff2d5518' : healthAmber ? '#e6b80018' : '#30d15818';
      const healthBdr   = healthRed ? '#ff2d5540' : healthAmber ? '#e6b80040' : '#30d15840';
      pills.push(
        <span key="battery-health" style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:10,
          padding:'2px 7px',borderRadius:4,background:healthBg,border:`1px solid ${healthBdr}`,color:healthColor}}>
          Battery Health {Math.round(health)}%
        </span>
      );
    }
  }

  if (!pills.length) return null;
  return (
    <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:4}}>
      {pills}
    </div>
  );
}


// ── iperf3 performance panel ──────────────────────────────────────────────────
function IperfSparkline({ points, color, height }) {
  if (!points || points.length < 2) return null;
  const W = 600; // viewBox width — scales to container
  const H = height * 4; // viewBox height proportional
  const max = Math.max(...points, 1);
  const min = Math.min(...points);
  const range = (max - min) || max || 1;
  const pad = 8;
  const w = W - pad * 2;
  const h = H - pad * 2;
  const coords = points.map((v, i) => {
    const x = pad + (i / (points.length - 1)) * w;
    const y = pad + h - ((v - min) / range) * h;
    return `${x},${y}`;
  });
  // Add area fill under line
  const firstX = pad, lastX = pad + w;
  const areaPoints = `${firstX},${H - pad} ${coords.join(' ')} ${lastX},${H - pad}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={height} preserveAspectRatio="none" style={{display:'block'}}>
      <polygon points={areaPoints} fill={color} fillOpacity="0.08" />
      <polyline points={coords.join(' ')} fill="none" stroke={color} strokeWidth="3" strokeLinejoin="round" />
    </svg>
  );
}

function IperfPanel({ slug, deviceId, isAdmin }) {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [triggered, setTriggered]   = useState(false);

  useEffect(() => {
    authFetch(`/api/clients/${slug}/endpoint/device/${deviceId}/iperf`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, deviceId]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await authFetch(`/api/clients/${slug}/endpoint/device/${deviceId}/iperf-trigger`, { method: 'POST' });
      setTriggered(true);
      setTimeout(() => setTriggered(false), 5000);
    } finally {
      setTriggering(false);
    }
  };

  const targetColors = { 'STG Services': '#00d4ff', 'Local Network': '#30d158' };

  if (loading) return (
    <div style={{padding:'10px 14px',fontSize:11,color:'var(--text-muted)',borderTop:'1px solid var(--border)'}}>
      Loading performance data...
    </div>
  );

  const latest = data?.history?.[0];
  const history = (data?.history||[]).slice().reverse(); // oldest first for chart
  const allTargets = [...new Set(history.flatMap(h => h.results?.filter(r=>r.status==='ok').map(r=>r.label)||[]))];

  return (
    <div style={{padding:'10px 14px',borderTop:'1px solid var(--border)',background:'rgba(0,212,255,0.02)'}}>
      {/* Latest summary row */}
      <div style={{display:'flex',flexWrap:'wrap',gap:6,alignItems:'center',marginBottom:8}}>
        <span style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginRight:4}}>Network Performance</span>
        {latest?.results?.filter(r => r.status === 'ok').map(r => (
          <span key={r.label} style={{fontSize:11,padding:'2px 7px',borderRadius:4,
            background:'var(--surface)',border:`1px solid ${targetColors[r.label]||'var(--accent)'}40`}}>
            <span style={{color:targetColors[r.label]||'var(--accent)',fontWeight:600,marginRight:4}}>{r.label}</span>
            <span>↓{r.download_mbps?.toFixed(0)} ↑{r.upload_mbps?.toFixed(0)} Mbps</span>
            {r.latency_ms > 0 && <span style={{color:'var(--text-muted)',marginLeft:4}}>{r.latency_ms?.toFixed(0)}ms</span>}
          </span>
        ))}
        {latest?.results?.filter(r => r.status === 'off_site').map(r => (
          <span key={r.label} style={{fontSize:11,padding:'2px 7px',borderRadius:4,background:'var(--surface)',border:'1px solid var(--border)',color:'var(--text-muted)'}}>
            {r.label}: Not on company site
          </span>
        ))}
        {latest?.results?.filter(r => r.status === 'blocked').map(r => (
          <span key={r.label} style={{fontSize:11,padding:'2px 7px',borderRadius:4,background:'#e6b80018',border:'1px solid #e6b80040',color:'#e6b800'}}>
            {r.label}: Blocked
          </span>
        ))}
        {!latest && <span style={{fontSize:11,color:'var(--text-muted)'}}>No data yet — tests run every 4 hours.</span>}
        {isAdmin && (
          <button className="btn btn-secondary btn-sm" aria-pressed={!(triggered)} style={{ marginLeft: 'auto' }} onClick={handleTrigger} disabled={triggering||triggered}
           >
            {triggered ? '✓ Queued' : triggering ? '...' : '▶ Run Test'}
          </button>
        )}
      </div>

      {/* Sparkline charts per target */}
      {history.length > 1 && allTargets.length > 0 && (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {allTargets.map(label => {
            const dlPoints  = history.map(h => h.results?.find(r=>r.label===label&&r.status==='ok')?.download_mbps||null).filter(v=>v!==null);
            const pingPoints = history.map(h => h.results?.find(r=>r.label===label&&r.status==='ok')?.latency_ms||null).filter(v=>v!==null);
            if (!dlPoints.length) return null;
            return (
              <div key={label}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:2}}>
                  <span style={{width:8,height:8,borderRadius:'50%',background:targetColors[label]||'var(--accent)',flexShrink:0,display:'inline-block'}}/>
                  <span style={{fontSize:10,fontWeight:600,color:targetColors[label]||'var(--accent)'}}>{label}</span>
                  <span style={{fontSize:10,color:'var(--text-muted)'}}>↓ Download Mbps</span>
                </div>
                <IperfSparkline points={dlPoints} color={targetColors[label]||'var(--accent)'} height={50} />
                {pingPoints.length > 1 && pingPoints.some(p=>p>0) && (
                  <div style={{marginTop:2}}>
                    <span style={{fontSize:10,color:'var(--text-muted)'}}>Ping ms</span>
                    <IperfSparkline points={pingPoints} color={'#86868b'} height={30} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function OsquerySecurityPanel({ slug, deviceId, isAdmin }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [triggered, setTriggered] = useState(false);
  const [expandedCve, setExpandedCve] = useState(null);
  const [showAllPorts, setShowAllPorts] = useState(false);

  useEffect(() => {
    authFetch(`/api/clients/${slug}/endpoint/device/${deviceId}/osquery-scan`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, deviceId]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await authFetch(`/api/clients/${slug}/endpoint/device/${deviceId}/osquery-scan-trigger`, { method: 'POST' });
      setTriggered(true);
      setTimeout(() => setTriggered(false), 5000);
    } finally {
      setTriggering(false);
    }
  };

  const cvssColor = (cvss) => {
    if (cvss >= 9) return '#ff2d55';
    if (cvss >= 7) return '#ff9f0a';
    if (cvss >= 4) return '#e6b800';
    return '#30d158';
  };

  const scStateColor = (item) => {
    const s = (item.state || '').toLowerCase();
    if (s === 'good' || s === 'on') return '#30d158';
    if (s === 'warn') return '#e6b800';
    if (s.includes('off') || s.includes('disabled')) return '#ff2d55';
    return '#e6b800';
  };

  if (loading) return (
    <div style={{padding:'10px 14px',fontSize:11,color:'var(--text-muted)',borderTop:'1px solid var(--border)'}}>
      Loading security scan...
    </div>
  );

  if (!data?.available) return (
    <div style={{padding:'10px 14px',borderTop:'1px solid var(--border)'}}>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em'}}>Security Scan</span>
        <span style={{fontSize:11,color:'var(--text-muted)'}}>No scan data yet — scans run daily.</span>
        {isAdmin && (
          <button className="btn btn-secondary btn-sm" onClick={handleTrigger} disabled={triggering||triggered} style={{marginLeft:'auto', color: triggered ? 'var(--green-ink)' : undefined}}>
            {triggered ? '✓ Queued' : triggering ? '...' : '▶ Run Scan'}
          </button>
        )}
      </div>
    </div>
  );

  const cveFindings  = (data.cve_findings || []).filter(f => f.cves?.length > 0);
  const enriched     = !!data.cve_enriched_at;
  const criticalCnt  = cveFindings.reduce((a, f) => a + f.cves.filter(c => c.cvss >= 9).length, 0);
  const highCnt      = cveFindings.reduce((a, f) => a + f.cves.filter(c => c.cvss >= 7 && c.cvss < 9).length, 0);
  const secCenter    = (data.security_center || []);
  const localAdmins  = (data.local_admins || []);
  const ports        = (data.ports || []);
  const patches      = (data.patches || []);

  const scannedAt = data.scanned_at
    ? new Date(data.scanned_at).toLocaleString('en-GB', {
        day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', timeZone:'Europe/London'
      }) + ' GMT'
    : '';

  return (
    <div style={{padding:'10px 14px',borderTop:'1px solid var(--border)'}}>

      {/* Header row */}
      <div style={{display:'flex',flexWrap:'wrap',gap:6,alignItems:'center',marginBottom:10}}>
        <span style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginRight:4}}>
          Security Scan
        </span>
        {enriched && criticalCnt > 0 && (
          <span style={{fontSize:11,padding:'2px 7px',borderRadius:4,background:'#ff2d5518',border:'1px solid #ff2d5540',color:'#ff2d55',fontWeight:600}}>
            {criticalCnt} Critical
          </span>
        )}
        {enriched && highCnt > 0 && (
          <span style={{fontSize:11,padding:'2px 7px',borderRadius:4,background:'#ff9f0a18',border:'1px solid #ff9f0a40',color:'#ff9f0a',fontWeight:600}}>
            {highCnt} High
          </span>
        )}
        {enriched && criticalCnt === 0 && highCnt === 0 && (
          <span style={{fontSize:11,padding:'2px 7px',borderRadius:4,background:'#30d15818',border:'1px solid #30d15840',color:'#30d158',fontWeight:600}}>
            No CVE findings
          </span>
        )}
        {!enriched && (
          <span style={{fontSize:11,color:'var(--text-muted)'}}>CVE analysis pending...</span>
        )}
        {scannedAt && (
          <span style={{fontSize:10,color:'var(--text-muted)',marginLeft:2}}>Updated {scannedAt}</span>
        )}
        {isAdmin && (
          <button className="btn btn-secondary btn-sm" onClick={handleTrigger} disabled={triggering||triggered} style={{marginLeft:'auto', color: triggered ? 'var(--green-ink)' : undefined}}>
            {triggered ? '✓ Queued' : triggering ? '...' : '▶ Run Scan'}
          </button>
        )}
      </div>

      {/* Security Center tiles */}
      {secCenter.length > 0 && (
        <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:10}}>
          {secCenter.map((item, i) => {
            const col = scStateColor(item);
            return (
              <div key={i} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 10px',
                borderRadius:6,border:`1px solid ${col}40`,background:`${col}10`}}>
                <span style={{width:6,height:6,borderRadius:'50%',background:col,flexShrink:0}}/>
                <span style={{fontSize:11,color:'var(--text)',fontWeight:500,maxWidth:180,overflow:'hidden',
                  textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.name}</span>
                {!item.signatures_up_to_date && (
                  <span style={{fontSize:10,color:'#e6b800',fontWeight:600,flexShrink:0}}>Defs outdated</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Local Admins */}
      {localAdmins.length > 0 && (
        <div style={{marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',
            letterSpacing:'0.07em',marginBottom:4}}>
            Local Administrators ({localAdmins.length})
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
            {localAdmins.map((u, i) => (
              <span key={i} style={{fontSize:11,padding:'2px 8px',borderRadius:4,
                background:'rgba(0,212,255,0.08)',border:'1px solid rgba(0,212,255,0.2)',color:'var(--text)'}}>
                {u.username}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* CVE Findings */}
      {enriched && cveFindings.length > 0 && (
        <div style={{marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:4}}>
            Potential CVE Findings
            <span style={{fontWeight:400,textTransform:'none',marginLeft:6,fontSize:10,opacity:0.7}}>
              based on installed software — verify applicability to installed version
            </span>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3}}>
            {cveFindings.map((finding, fi) => {
              const isExp   = expandedCve === fi;
              const topCvss = finding.cves?.[0]?.cvss || 0;
              const topCol  = cvssColor(topCvss);
              return (
                <div key={fi} style={{borderRadius:6,border:'1px solid var(--border)',overflow:'hidden'}}>
                  <div onClick={() => setExpandedCve(isExp ? null : fi)}
                    style={{display:'flex',alignItems:'center',gap:8,padding:'5px 10px',cursor:'pointer',
                      background:isExp?'rgba(0,212,255,0.03)':'transparent'}}>
                    <span style={{fontSize:11,fontWeight:600,color:'var(--text)',flex:1,
                      overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {finding.program}
                    </span>
                    <span style={{fontSize:10,color:'var(--text-muted)',flexShrink:0}}>{finding.version}</span>
                    <span style={{fontSize:10,padding:'1px 6px',borderRadius:3,flexShrink:0,fontWeight:600,
                      background:`${topCol}18`,border:`1px solid ${topCol}40`,color:topCol}}>
                      {finding.cves.length} CVE{finding.cves.length !== 1 ? 's' : ''} · {finding.cves[0]?.severity}
                    </span>
                    <span style={{fontSize:10,color:'var(--text-muted)'}}>{isExp ? '▲' : '▼'}</span>
                  </div>
                  {isExp && (
                    <div style={{borderTop:'1px solid var(--border)',background:'var(--surface)'}}>
                      {finding.cves.map((cve, ci) => (
                        <div key={ci} style={{display:'flex',alignItems:'flex-start',gap:8,padding:'5px 10px',
                          borderBottom:ci < finding.cves.length - 1 ? '1px solid var(--border)' : 'none'}}>
                          <span style={{fontSize:10,padding:'1px 5px',borderRadius:3,flexShrink:0,fontWeight:600,
                            background:cvssColor(cve.cvss)+'18',border:`1px solid ${cvssColor(cve.cvss)}40`,
                            color:cvssColor(cve.cvss),minWidth:36,textAlign:'center'}}>
                            {cve.cvss.toFixed(1)}
                          </span>
                          <span style={{fontSize:10,fontFamily:'var(--font-mono)',color:'var(--accent)',
                            flexShrink:0,minWidth:100}}>{cve.id}</span>
                          <span style={{fontSize:10,color:'var(--text)',overflow:'hidden',
                            textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{cve.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {enriched && cveFindings.length === 0 && patches.length > 0 && (
        <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:10}}>
          {patches.length} Windows patch{patches.length !== 1 ? 'es' : ''} installed · no CVE matches found
        </div>
      )}

      {/* Listening Ports */}
      {ports.length > 0 && (
        <div>
          <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',
            letterSpacing:'0.07em',marginBottom:4}}>
            Listening Ports ({ports.length})
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            {(showAllPorts ? ports : ports.slice(0, 12)).map((p, i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'3px 8px',
                borderRadius:4,background:'var(--surface)',border:'1px solid var(--border)'}}>
                <span style={{fontSize:11,fontFamily:'var(--font-mono)',color:'var(--accent)',minWidth:65,flexShrink:0}}>
                  {p.port}/{p.protocol}
                </span>
                <span style={{fontSize:11,color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {p.process || 'unknown'}
                </span>
              </div>
            ))}
            {ports.length > 12 && (
              <button className="btn btn-quiet btn-sm" style={{ textAlign: 'left' }} onClick={() => setShowAllPorts(!showAllPorts)}
               >
                {showAllPorts ? 'Show less' : `+${ports.length - 12} more ports`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ScreenshotPanel({ slug, deviceId }) {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [triggered, setTriggered]   = useState(false);
  const [polling, setPolling]       = useState(false);
  const [lightbox, setLightbox]     = useState(false);
  const pollRef                     = useRef(null);

  const fetchScreenshot = () =>
    authFetch(`/api/clients/${slug}/endpoint/device/${deviceId}/screenshot`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); return d; })
      .catch(() => { setLoading(false); });

  useEffect(() => {
    fetchScreenshot();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [slug, deviceId]);

  // Close lightbox on Escape
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => { if (e.key === 'Escape') setLightbox(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await authFetch(`/api/clients/${slug}/endpoint/device/${deviceId}/screenshot-trigger`, { method: 'POST' });
      setTriggered(true);
      setPolling(true);
      // Poll every 10 seconds for up to 3 minutes waiting for the agent to respond
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts++;
        const d = await fetchScreenshot();
        const capturedAt = d?.captured_at;
        const isNew = capturedAt && (Date.now() - new Date(capturedAt).getTime() < 180000);
        if (isNew || attempts >= 18) {
          clearInterval(pollRef.current);
          setPolling(false);
          setTriggered(false);
        }
      }, 10000);
    } finally {
      setTriggering(false);
    }
  };

  if (loading) return (
    <div style={{padding:'10px 14px',fontSize:11,color:'var(--text-muted)',borderTop:'1px solid var(--border)'}}>
      Loading screenshot...
    </div>
  );

  const captured = data?.captured_at
    ? new Date(data.captured_at).toLocaleString('en-GB', {day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'}) + ' GMT'
    : null;

  const imgSrc = data?.image_b64 ? `data:image/jpeg;base64,${data.image_b64}` : null;

  return (
    <div style={{padding:'10px 14px',borderTop:'1px solid var(--border)',background:'rgba(0,212,255,0.02)'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:imgSrc ? 10 : 0}}>
        <span style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em'}}>Screenshot</span>
        {captured && !polling && (
          <span style={{fontSize:10,color:'var(--text-muted)'}}>Updated {captured}</span>
        )}
        {polling && (
          <span style={{fontSize:10,color:'var(--accent)'}}>Waiting for agent response...</span>
        )}
        {!imgSrc && !polling && (
          <span style={{fontSize:11,color:'var(--text-muted)'}}>No screenshot available — request one below.</span>
        )}
        <button className="btn btn-secondary btn-sm" aria-pressed={!(polling)} style={{ marginLeft: 'auto' }} onClick={handleTrigger} disabled={triggering||polling}
         >
          {polling ? '⏳ Waiting...' : triggering ? '...' : ' Take Screenshot'}
        </button>
      </div>

      {/* Thumbnail — click to expand */}
      {imgSrc && (
        <div onClick={() => setLightbox(true)}
          style={{borderRadius:6,overflow:'hidden',border:'1px solid var(--border)',cursor:'zoom-in',position:'relative'}}>
          <img
            src={imgSrc}
            alt="Device screenshot"
            style={{width:'100%',display:'block',maxHeight:200,objectFit:'contain',background:'#000'}}
          />
          <div style={{position:'absolute',bottom:6,right:8,fontSize:10,color:'rgba(255,255,255,0.6)',
            background:'rgba(0,0,0,0.5)',padding:'2px 6px',borderRadius:3,pointerEvents:'none'}}>
            Click to expand
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && imgSrc && (
        <div onClick={() => setLightbox(false)}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.92)',zIndex:2000,
            display:'flex',alignItems:'center',justifyContent:'center',padding:16,cursor:'zoom-out'}}>
          <div onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:'95vw',maxHeight:'95vh'}}>
            <img
              src={imgSrc}
              alt="Device screenshot fullscreen"
              style={{display:'block',maxWidth:'95vw',maxHeight:'92vh',objectFit:'contain',borderRadius:6,
                border:'1px solid rgba(255,255,255,0.1)'}}
            />
            <div style={{position:'absolute',top:-10,right:-10}}>
              <button className="btn btn-secondary btn-sm btn-icon" onClick={() => setLightbox(false)}
               >
                ×
              </button>
            </div>
            {captured && (
              <div style={{textAlign:'center',fontSize:11,color:'rgba(255,255,255,0.4)',marginTop:8}}>
                {captured}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RebootPanel({ slug, deviceId, dev }) {
  const [status, setStatus]       = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [rebooting, setRebooting] = useState(false);
  const [rebooted, setRebooted]   = useState(false);
  const pollRef                   = useRef(null);

  const fetchStatus = () =>
    authFetch(`/api/clients/${slug}/endpoint/device/${deviceId}/reboot-status`)
      .then(r => r.json())
      .then(d => { setStatus(d); return d; })
      .catch(() => {});

  useEffect(() => {
    fetchStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [slug, deviceId]);

  const handleReboot = async () => {
    setConfirming(false);
    setRebooting(true);
    try {
      await authFetch(`/api/clients/${slug}/endpoint/device/${deviceId}/reboot-trigger`, { method: 'POST' });
      setRebooted(true);
      // Poll for ack every 5 seconds for up to 3 minutes
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts++;
        const d = await fetchStatus();
        if (d?.ack_at || attempts >= 36) {
          clearInterval(pollRef.current);
          setRebooting(false);
        }
      }, 5000);
    } catch {
      setRebooting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString('en-GB', {
        day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', timeZone:'Europe/London',
      }) + ' GMT';
    } catch { return iso; }
  };

  const isQueued  = status?.queued;
  const isPending = status?.pending_at && !status?.ack_at;
  const isAcked   = !!status?.ack_at;
  const osRebootPending = dev?.pending_reboot?.pending;

  return (
    <div style={{padding:'10px 14px',borderTop:'1px solid var(--border)',background:'rgba(230,184,0,0.02)'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <span style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em'}}>Remote Reboot</span>

        {/* OS-level pending reboot indicator */}
        {osRebootPending && (
          <span style={{fontSize:11,padding:'2px 7px',borderRadius:4,background:'#e6b80018',border:'1px solid #e6b80040',color:'#e6b800',fontWeight:600}}>
            OS reboot required{dev.pending_reboot.reason ? ' · ' + dev.pending_reboot.reason : ''}
          </span>
        )}

        {/* Admin reboot state */}
        {isAcked && (
          <span style={{fontSize:11,color:'var(--text-muted)'}}>
            Reboot acknowledged {formatDate(status.ack_at)}{status.eta_seconds ? ` — ${status.eta_seconds}s delay` : ''}
          </span>
        )}
        {isPending && !isAcked && (
          <span style={{fontSize:11,color:'var(--accent)'}}>Reboot instruction sent — waiting for agent...</span>
        )}
        {isQueued && !isPending && (
          <span style={{fontSize:11,color:'var(--accent)'}}>Reboot queued — will execute on next heartbeat</span>
        )}
        {!osRebootPending && !isQueued && !isPending && !isAcked && !rebooted && (
          <span style={{fontSize:11,color:'var(--text-muted)'}}>No reboot pending.</span>
        )}

        {/* Reboot button */}
        {!confirming && !rebooting && (
          <button className="btn btn-secondary btn-sm" onClick={() => setConfirming(true)} disabled={isQueued || isPending} style={{marginLeft:'auto'}}>
            ↺ Reboot Device
          </button>
        )}
        {rebooting && (
          <span style={{fontSize:11,color:'var(--text-muted)',marginLeft:'auto'}}>Waiting for agent...</span>
        )}

        {/* Confirmation prompt */}
        {confirming && (
          <div style={{marginLeft:'auto',display:'flex',gap:6,alignItems:'center'}}>
            <span style={{fontSize:11,color:'#e6b800',fontWeight:600}}>Reboot {dev?.display_name || dev?.hostname || 'this device'}?</span>
            <button className="btn btn-secondary btn-sm" style={{ color: '#e6b800' }} onClick={handleReboot}
             >
              Confirm
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setConfirming(false)}
             >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ServiceStatusRow({ statuses = [] }) {
  if (!statuses || statuses.length === 0) return null;
  const colour = s => s === 'running' ? '#30d158' : s === 'stopped' ? '#e6b800' : '#ff2d55';
  const label  = s => s === 'running' ? 'Running' : s === 'stopped' ? 'Stopped' : 'Not Installed';
  return (
    <div style={{padding:'8px 14px',borderTop:'1px solid var(--border)',display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
      <span style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginRight:4}}>Services</span>
      {statuses.map(svc => (
        <span key={svc.name} style={{fontSize:11,padding:'2px 7px',borderRadius:4,
          background:`${colour(svc.status)}18`,border:`1px solid ${colour(svc.status)}40`,
          color:colour(svc.status),fontWeight:600,display:'flex',alignItems:'center',gap:4}}>
          <span style={{width:6,height:6,borderRadius:'50%',background:colour(svc.status),display:'inline-block'}}/>
          {svc.display || svc.name}: {label(svc.status)}
        </span>
      ))}
    </div>
  );
}

function AgentUpdatePanel({ slug, deviceId, dev }) {
  const [queued, setQueued]       = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy]           = useState(false);
  const [published, setPublished] = useState(null);

  useEffect(() => {
    authFetch('/api/admin/agent-update/published')
      .then(r => r.json())
      .then(d => setPublished(d.published || null))
      .catch(() => {});
  }, []);

  const handleForceUpdate = async () => {
    setConfirming(false);
    setBusy(true);
    try {
      const r = await authFetch(`/api/clients/${slug}/endpoint/device/${deviceId}/force-update`, { method: 'POST' });
      if (r.ok) setQueued(true);
      else { const d = await r.json(); alert(d.error || 'Failed to queue update'); }
    } catch { alert('Failed to queue update'); }
    setBusy(false);
  };

  const deviceVersion   = dev?.agent_version || '—';
  const publishedVersion = published?.version || null;
  const upToDate = publishedVersion && deviceVersion === publishedVersion;

  return (
    <div style={{padding:'10px 14px',borderTop:'1px solid var(--border)',background:'rgba(0,212,255,0.02)'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <span style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em'}}>Agent Update</span>

        <span style={{fontSize:11,color:'var(--text-muted)'}}>
          Device: <span style={{color:'var(--text)',fontWeight:600}}>{deviceVersion}</span>
          {publishedVersion && (
            <> · Published: <span style={{color: upToDate ? 'var(--green)' : 'var(--accent)', fontWeight:600}}>{publishedVersion}</span></>
          )}
        </span>

        {upToDate && !queued && (
          <span style={{fontSize:11,padding:'2px 7px',borderRadius:4,background:'#30d15818',border:'1px solid #30d15840',color:'#30d158',fontWeight:600}}>Up to date</span>
        )}
        {queued && (
          <span style={{fontSize:11,color:'var(--accent)'}}>Update queued — will apply on next heartbeat</span>
        )}

        {!confirming && !busy && !queued && (
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
            onClick={() => setConfirming(true)}
           >
            ↑ Force Update
          </button>
        )}
        {busy && <span style={{fontSize:11,color:'var(--text-muted)',marginLeft:'auto'}}>Queuing...</span>}

        {confirming && (
          <div style={{marginLeft:'auto',display:'flex',gap:6,alignItems:'center'}}>
            <span style={{fontSize:11,color:'var(--accent)',fontWeight:600}}>Force update {dev?.display_name || dev?.hostname || 'this device'} to v{publishedVersion || '?'}?</span>
            <button className="btn btn-secondary btn-sm" onClick={handleForceUpdate}
             >
              Confirm
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setConfirming(false)}
             >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Software Inventory Tab ────────────────────────────────────────────────────
function SoftwareInventoryTab({ dev }) {
  const [filter, setFilter] = useState('');
  const inv = dev.software_inventory || null;

  if (!inv) {
    return (
      <div style={{padding:'14px',fontSize:12,color:'var(--text-muted)'}}>
        Software inventory not yet collected — will appear after the next daily agent cycle.
      </div>
    );
  }

  const items = inv.items || [];
  const filtered = filter
    ? items.filter(i =>
        i.name?.toLowerCase().includes(filter.toLowerCase()) ||
        i.publisher?.toLowerCase().includes(filter.toLowerCase()) ||
        i.version?.toLowerCase().includes(filter.toLowerCase())
      )
    : items;

  return (
    <div style={{padding:'14px'}}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:12,flexWrap:'wrap'}}>
        <div style={{fontSize:11,color:'var(--text-muted)'}}>
          {inv.count} application{inv.count !== 1 ? 's' : ''} installed
          {' · '}collected {inv.collected_at
            ? new Date(inv.collected_at).toLocaleString('en-GB',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'}) + ' GMT'
            : 'unknown'}
        </div>
        <input className="input-sm"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter by name, publisher or version…"
          style={{ flex:1, minWidth:180 }}
        />
        {filter && (
          <span style={{fontSize:11,color:'var(--text-muted)'}}>
            {filtered.length} of {items.length}
          </span>
        )}
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
          <thead>
            <tr style={{borderBottom:'1px solid var(--border)'}}>
              {['Name','Version','Publisher','Installed'].map(h => (
                <th key={h} style={{textAlign:'left',padding:'4px 8px',fontSize:10,fontWeight:700,
                  color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',
                  whiteSpace:'nowrap'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={4} style={{padding:'12px 8px',color:'var(--text-muted)',fontSize:11}}>
                No matching applications.
              </td></tr>
            ) : filtered.map((item, i) => (
              <tr key={i} style={{borderBottom:'1px solid var(--border)',
                background:i%2===0?'transparent':'var(--bg-secondary)'}}>
                <td style={{padding:'5px 8px',color:'var(--text-primary)',fontWeight:500}}>{item.name}</td>
                <td style={{padding:'5px 8px',color:'var(--text-muted)',fontFamily:'monospace',fontSize:10}}>{item.version||'—'}</td>
                <td style={{padding:'5px 8px',color:'var(--text-muted)'}}>{item.publisher||'—'}</td>
                <td style={{padding:'5px 8px',color:'var(--text-muted)',whiteSpace:'nowrap'}}>{item.install_date||'—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Hardware Inventory Tab ────────────────────────────────────────────────────
// Renders the full daily hardware inventory (CPU, RAM, Storage, GPU, TPM, Secure Boot)
// alongside the basic hardware fields already stored in dev.hardware.
function HardwareInventoryTab({ dev, slug, isAdmin }) {
  const hw  = dev.hardware || {};
  const inv = dev.hardware_inventory || null;

  const HWSection = ({ title, children }) => (
    <div style={{width:'100%',marginBottom:12}}>
      <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',
        letterSpacing:'0.08em',marginBottom:8,paddingBottom:4,borderBottom:'1px solid var(--border)'}}>
        {title}
      </div>
      <div style={{display:'flex',flexWrap:'wrap',gap:16}}>{children}</div>
    </div>
  );

  const Chip = ({ label, value, mono }) => {
    if (!value && value !== 0 && value !== false) return null;
    const display = value === true ? 'Yes' : value === false ? 'No' : String(value);
    return (
      <div style={{minWidth:100}}>
        <div style={{fontSize:9,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',
          letterSpacing:'0.07em',marginBottom:2}}>{label}</div>
        <div style={{fontSize:12,color:'var(--text-primary)',fontFamily:mono?'monospace':'inherit'}}>{display}</div>
      </div>
    );
  };

  const tpmColor = inv?.tpm?.present
    ? (inv.tpm.enabled && inv.tpm.activated ? '#30d158' : '#e6b800')
    : '#ff2d55';
  const sbColor = inv?.secure_boot === true ? '#30d158' : inv?.secure_boot === false ? '#ff2d55' : 'var(--text-muted)';

  return (
    <div style={{padding:'14px'}}>
      {/* Always-available basic fields */}
      <HWSection title="System">
        <Chip label="Manufacturer" value={hw.manufacturer} />
        <Chip label="Model" value={hw.model} />
        <Chip label="Service Tag" value={hw.service_tag} mono />
        <Chip label="BIOS Version" value={hw.bios_version} mono />
        <Chip label="BIOS Date" value={hw.bios_release_date} mono />
        {dev.battery?.health_pct != null && <Chip label="Battery Health" value={`${dev.battery.health_pct}%`} />}
      </HWSection>

      {!hw.manufacturer && !hw.model && !hw.service_tag && !inv && (
        <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:12}}>No hardware data reported yet.</div>
      )}

      {inv ? (
        <>
          {/* CPU */}
          {inv.cpu?.length > 0 && (
            <HWSection title="Processor">
              {inv.cpu.map((c, i) => (
                <div key={i} style={{display:'flex',flexWrap:'wrap',gap:16,width:'100%',
                  paddingBottom:inv.cpu.length>1?8:0,borderBottom:inv.cpu.length>1&&i<inv.cpu.length-1?'1px dashed var(--border)':'none'}}>
                  <Chip label="CPU" value={c.name} />
                  <Chip label="Cores" value={c.cores ? `${c.cores}C / ${c.logical_cores}T` : null} />
                  <Chip label="Speed" value={c.speed_mhz ? `${(c.speed_mhz/1000).toFixed(1)} GHz` : null} />
                  <Chip label="Architecture" value={c.architecture} />
                </div>
              ))}
            </HWSection>
          )}

          {/* RAM */}
          {inv.ram?.length > 0 && (
            <HWSection title={`Memory${inv.total_ram_gb ? ` (${inv.total_ram_gb} GB total)` : ''}`}>
              {inv.ram.map((r, i) => (
                <div key={i} style={{display:'flex',flexWrap:'wrap',gap:12,width:'100%',
                  paddingBottom:8,borderBottom:i<inv.ram.length-1?'1px dashed var(--border)':'none'}}>
                  <Chip label="Slot" value={r.slot} mono />
                  <Chip label="Capacity" value={r.capacity_gb ? `${r.capacity_gb} GB` : null} />
                  <Chip label="Type" value={r.memory_type} />
                  <Chip label="Speed" value={r.speed_mhz ? `${r.speed_mhz} MHz` : null} />
                  <Chip label="Manufacturer" value={r.manufacturer} />
                  <Chip label="Part Number" value={r.part_number} mono />
                </div>
              ))}
            </HWSection>
          )}

          {/* Storage */}
          {inv.storage?.length > 0 && (
            <HWSection title="Storage">
              {inv.storage.map((d, i) => (
                <div key={i} style={{display:'flex',flexWrap:'wrap',gap:12,width:'100%',
                  paddingBottom:8,borderBottom:i<inv.storage.length-1?'1px dashed var(--border)':'none'}}>
                  <Chip label="Drive" value={d.model} />
                  <Chip label="Type" value={d.type || d.media_type} />
                  <Chip label="Interface" value={d.interface} />
                  <Chip label="Capacity" value={d.capacity_gb ? `${d.capacity_gb} GB` : null} />
                  <Chip label="Serial" value={d.serial_number} mono />
                </div>
              ))}
            </HWSection>
          )}

          {/* GPU */}
          {inv.gpu?.length > 0 && (
            <HWSection title="Graphics">
              {inv.gpu.map((g, i) => (
                <div key={i} style={{display:'flex',flexWrap:'wrap',gap:12,width:'100%',
                  paddingBottom:8,borderBottom:i<inv.gpu.length-1?'1px dashed var(--border)':'none'}}>
                  <Chip label="GPU" value={g.name} />
                  <Chip label="VRAM" value={g.vram_gb ? `${g.vram_gb} GB` : null} />
                  <Chip label="Driver" value={g.driver_version} mono />
                </div>
              ))}
            </HWSection>
          )}

          {/* Displays */}
          {inv.displays?.length > 0 && (
            <HWSection title="Displays">
              {inv.displays.map((d, i) => (
                <div key={i} style={{display:'flex',flexWrap:'wrap',gap:12,width:'100%',
                  paddingBottom:8,borderBottom:i<inv.displays.length-1?'1px dashed var(--border)':'none'}}>
                  <Chip label="Display" value={d.name} />
                  <Chip label="Manufacturer" value={d.manufacturer} />
                  {d.width_px > 0 && <Chip label="Resolution" value={`${d.width_px} × ${d.height_px}`} />}
                </div>
              ))}
            </HWSection>
          )}

          {/* Security — TPM and Secure Boot */}
          {(inv.tpm != null || inv.secure_boot != null) && (
            <HWSection title="Platform Security">
              {inv.tpm && (
                <>
                  <div style={{minWidth:100}}>
                    <div style={{fontSize:9,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:2}}>TPM</div>
                    <div style={{fontSize:12,color:tpmColor}}>
                      {inv.tpm.present
                        ? `Present${inv.tpm.spec_version ? ` (${inv.tpm.spec_version})` : ''}${inv.tpm.enabled && inv.tpm.activated ? '' : ' — not active'}`
                        : 'Not detected'}
                    </div>
                  </div>
                  {inv.tpm.manufacturer_id && <Chip label="TPM Manufacturer" value={inv.tpm.manufacturer_id} />}
                </>
              )}
              {inv.secure_boot != null && (
                <div style={{minWidth:100}}>
                  <div style={{fontSize:9,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:2}}>Secure Boot</div>
                  <div style={{fontSize:12,color:sbColor}}>{inv.secure_boot ? 'Enabled' : 'Disabled'}</div>
                </div>
              )}
            </HWSection>
          )}

          <div style={{fontSize:10,color:'var(--text-muted)',marginTop:4}}>
            Inventory collected {inv.collected_at ? new Date(inv.collected_at).toLocaleString('en-GB',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'}) + ' GMT' : 'unknown'}
          </div>
        </>
      ) : (
        hw.manufacturer || hw.model ? (
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>
            Full inventory not yet collected — will appear after the next daily agent cycle.
          </div>
        ) : null
      )}
    </div>
  );
}

const DEVICE_CARD_TABS = [
  { id: 'overview',     label: 'Overview' },
  { id: 'hardware',     label: 'Hardware' },
  { id: 'software',     label: 'Software' },
  { id: 'connectivity', label: 'Connectivity' },
  { id: 'security',     label: 'Security' },
  { id: 'activity',     label: 'Activity' },
];

function DeviceCardTabs({ slug, dev, isAdmin }) {
  const [tab, setTab] = useState('overview');
  const hw = dev.hardware || {};
  const adapters = hw.network_adapters || [];

  return (
    <div style={{borderTop:'1px solid var(--border)'}}>
      <div style={{display:'flex',gap:2,padding:'8px 14px 0',borderBottom:'1px solid var(--border)',overflowX:'auto'}}>
        {DEVICE_CARD_TABS.map(t => (
          <button className="tab-btn" aria-selected={tab===t.id} key={t.id} onClick={()=>setTab(t.id)}
           >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div style={{padding:'14px',display:'flex',flexWrap:'wrap',gap:20}}>
          <DetailField label="Model" value={[hw.manufacturer, hw.model].filter(Boolean).join(' ')} />
          <DetailField label="Location" value={[dev.geo?.city, dev.geo?.country].filter(Boolean).join(', ')} />
          <DetailField label="ISP" value={dev.geo?.isp} />
          <DetailField label="Logged In User" value={dev.logged_in_user} />
          <DetailField label="Uptime" value={formatUptime(dev.uptime_seconds)} />
          {dev.pending_reboot?.pending && (
            <DetailField label="Reboot Required" value={dev.pending_reboot.reason||'Yes'} color="#e6b800" />
          )}
          <DetailField label="Last Seen" value={formatDate(dev.last_seen)} mono />
        </div>
      )}

      {tab === 'hardware' && (
        <HardwareInventoryTab dev={dev} slug={slug} isAdmin={isAdmin} />
      )}

      {tab === 'software' && (
        <SoftwareInventoryTab dev={dev} slug={slug} />
      )}

      {tab === 'connectivity' && (
        <div>
          <IperfPanel slug={slug} deviceId={dev.device_id} isAdmin={isAdmin} />
          <div style={{padding:'14px',borderTop:'1px solid var(--border)'}}>
            <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:8}}>
              Network Adapters
            </div>
            {adapters.length === 0 ? (
              <div style={{fontSize:12,color:'var(--text-muted)'}}>No adapter data reported yet.</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {adapters.map((a, i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:10,fontSize:12,flexWrap:'wrap'}}>
                    <span style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:4,textTransform:'uppercase',letterSpacing:'0.05em',
                      background:a.type==='wireless'?'#00d4ff18':'#30d15818',color:a.type==='wireless'?'var(--accent)':'#30d158'}}>
                      {a.type==='wireless' ? 'Wi-Fi' : 'Wired'}
                    </span>
                    <span style={{fontWeight:600}}>{a.name||a.description||'Adapter'}</span>
                    <span style={{fontFamily:'var(--font-mono)',color:'var(--text-muted)'}}>{a.mac_address}</span>
                    {a.ssid && <span style={{color:'var(--text-muted)'}}>{'SSID: '+a.ssid}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'security' && (
        <div>
          <OsquerySecurityPanel slug={slug} deviceId={dev.device_id} isAdmin={isAdmin} />
        </div>
      )}

      {tab === 'activity' && (
        <div>
          <div style={{padding:'14px',display:'flex',flexWrap:'wrap',gap:20,borderBottom:'1px solid var(--border)'}}>
            <DetailField label="Last Seen" value={formatDate(dev.last_seen)} mono />
            {dev.last_compliance_check && (
              <DetailField label="Last Compliance Check" value={formatDate(dev.last_compliance_check)} mono />
            )}
            {dev.last_summary?.ce_plus && (
              <DetailField label="CE+ Score" value={`${dev.last_summary.ce_plus.pct}%`}
                color={dev.last_summary.ce_plus.grade==='pass'?'#30d158':dev.last_summary.ce_plus.grade==='fail'?'#ff2d55':'var(--text-muted)'} />
            )}
          </div>
          {isAdmin && <ScreenshotPanel slug={slug} deviceId={dev.device_id} />}
          {isAdmin && <RebootPanel slug={slug} deviceId={dev.device_id} dev={dev} />}
          {isAdmin && <AgentUpdatePanel slug={slug} deviceId={dev.device_id} dev={dev} />}
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value, mono, color }) {
  if (!value) return null;
  return (
    <div>
      <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:2}}>{label}</div>
      <div style={{fontSize:12,fontFamily:mono?'var(--font-mono)':undefined,color:color||'var(--text)'}}>{value}</div>
    </div>
  );
}

function formatUptime(seconds) {
  if (!seconds && seconds !== 0) return null;
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

// Sort order for the Clearview Agent device list is viewer-controlled and
// must stay stable across polling refreshes — never fall back to raw API
// order, which can reshuffle between fetches. Kept identical to the
// ClientManagement.js implementation for the admin view.
const AGENT_SORT_OPTIONS = [
  { key: 'name',      label: 'Name' },
  { key: 'status',    label: 'Status' },
  { key: 'last_seen', label: 'Last Seen' },
  { key: 'uptime',    label: 'Uptime' },
];

function agentDeviceName(d) {
  return (d.display_name || d.hostname || d.device_id || '').toLowerCase();
}

function compareAgentDevices(a, b, sortKey, sortDir) {
  const dir = sortDir === 'desc' ? -1 : 1;
  let cmp = 0;
  switch (sortKey) {
    case 'status': {
      const rank = s => s === 'online' ? 0 : s === 'offline' ? 1 : 2;
      cmp = rank(a.status) - rank(b.status);
      break;
    }
    case 'last_seen': {
      const ta = a.last_seen ? new Date(a.last_seen).getTime() : 0;
      const tb = b.last_seen ? new Date(b.last_seen).getTime() : 0;
      cmp = ta - tb;
      break;
    }
    case 'uptime':
      cmp = (a.uptime_seconds || 0) - (b.uptime_seconds || 0);
      break;
    case 'name':
    default:
      cmp = agentDeviceName(a).localeCompare(agentDeviceName(b));
      break;
  }
  if (cmp === 0) cmp = agentDeviceName(a).localeCompare(agentDeviceName(b));
  return cmp * dir;
}

function AgentSortBar({ sortKey, sortDir, onChange }) {
  return (
    <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
      <span style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',fontWeight:600}}>Sort:</span>
      {AGENT_SORT_OPTIONS.map(opt => {
        const active = sortKey === opt.key;
        return (
          <button className="btn btn-secondary btn-sm" aria-pressed={active} key={opt.key}
            onClick={() => onChange(opt.key, active && sortDir === 'asc' ? 'desc' : 'asc')}
           >
            {opt.label}{active && <span style={{fontSize:9}}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Agent Installer Panel ──────────────────────────────────────────────────────

function AgentInstallerPanel({ slug }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError]             = useState('');

  const download = async () => {
    setDownloading(true); setError('');
    try {
      const token = localStorage.getItem('stg_token');
      const res = await fetch(`/api/client/${slug}/agent/installer.ps1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to generate installer');
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `ClearviewAgent-${slug}.ps1`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); }
    finally { setDownloading(false); }
  };

  return (
    <div style={{background:'var(--card-bg)',border:'1px solid var(--border)',borderRadius:10,padding:'16px 18px'}}>
      <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8}}>
        Install Clearview Agent
      </div>
      <div style={{fontSize:12,color:'var(--text-muted)',lineHeight:1.5,marginBottom:12}}>
        Downloads a pre-configured PowerShell installer for this client.
        Right-click the file and select <strong>Run with PowerShell</strong> on the target device.
        The installer token is valid for 48 hours from download.
      </div>
      {error && <div className="error-msg" style={{marginBottom:10}}>{error}</div>}
      <button className="btn btn-primary" onClick={download} disabled={downloading}>
        {downloading ? <span className="spinner"/> : '↓ Download Installer (.ps1)'}
      </button>
    </div>
  );
}

function ClearviewAgentTab({ slug, isAdmin }) {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const showInstaller = user?.role === 'admin';
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selected,         setSelected]       = useState(null);
  const [confirming,       setConfirming]     = useState(null);
  const [actioning,        setActioning]      = useState(null);
  const [expandedDevice,   setExpandedDevice] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [renameModal, setRenameModal] = useState(null); // device object
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const handleArchive = async (deviceId) => {
    setActioning(deviceId);
    try {
      const r = await authFetch(`/api/clients/${slug}/endpoint/device/${deviceId}/archive`, { method: 'POST' });
      if (r.ok) { setSelected(null); load(); }
      else { const d = await r.json(); alert(d.error || 'Archive failed'); }
    } catch { alert('Archive failed'); }
    setActioning(null);
  };

  const handleDelete = async (deviceId) => {
    setConfirming(null);
    setActioning(deviceId);
    try {
      const r = await authFetch(`/api/clients/${slug}/endpoint/device/${deviceId}`, { method: 'DELETE' });
      if (r.ok) { setSelected(null); load(); }
      else { const d = await r.json(); alert(d.error || 'Delete failed'); }
    } catch { alert('Delete failed'); }
    setActioning(null);
  };

  const handleRename = async () => {
    if (!renameModal) return;
    setRenaming(true);
    try {
      const r = await authFetch(`/api/clients/${slug}/endpoint/device/${renameModal.device_id}/rename`, {
        method: 'POST',
        body: JSON.stringify({ display_name: renameValue.trim() }),
      });
      if (r.ok) {
        setRenameModal(null);
        setRenameValue('');
        load();
      } else { const d = await r.json(); alert(d.error || 'Rename failed'); }
    } catch { alert('Rename failed'); }
    setRenaming(false);
  };
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markersRef = useRef([]);

  const load = () => {
    authFetch(`/api/clients/${slug}/endpoint/devices`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [slug]);

  // Build/refresh Leaflet map once data is available
  useEffect(() => {
    if (!data?.devices?.length || !mapRef.current) return;
    const devices = data.devices.filter(d => d.geo?.lat && d.geo?.lng);
    if (!devices.length) return;

    if (!leafletMap.current) {
      leafletMap.current = window.L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false });
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors', maxZoom: 18,
      }).addTo(leafletMap.current);
      const tp = leafletMap.current.getPane('tilePane');
      if (tp) tp.style.filter = 'invert(1) hue-rotate(180deg) brightness(0.75) contrast(1.1)';
    }

    // Clear old markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    const bounds = [];
    const coordCount = {};
    devices.forEach(dev => { const key = `${dev.geo.lat},${dev.geo.lng}`; coordCount[key] = (coordCount[key]||0)+1; });
    const coordIndex = {};
    devices.forEach(dev => {
      const key = `${dev.geo.lat},${dev.geo.lng}`;
      const total = coordCount[key];
      const idx = coordIndex[key] = (coordIndex[key]||0);
      coordIndex[key]++;
      const angle = (2*Math.PI*idx)/Math.max(total,1);
      const offset = total > 1 ? 0.0003 : 0;
      const lat = dev.geo.lat + offset*Math.sin(angle);
      const lng = dev.geo.lng + offset*Math.cos(angle);
      const isOnline = dev.status === 'online';
      const color = isOnline ? '#30d158' : '#ff2d55';
      const icon = window.L.divIcon({
        className: '',
        html: `<div style="width:12px;height:12px;border-radius:50%;background:${color};border:2px solid rgba(255,255,255,0.6);box-shadow:0 0 6px ${color}80;"></div>`,
        iconSize: [12, 12], iconAnchor: [6, 6],
      });
      const marker = window.L.marker([lat, lng], { icon })
        .addTo(leafletMap.current)
        .on('click', () => setSelected(dev));
      markersRef.current.push(marker);
      bounds.push([lat, lng]);
    });

    if (bounds.length === 1) {
      leafletMap.current.setView(bounds[0], 10);
    } else if (bounds.length > 1) {
      leafletMap.current.fitBounds(bounds, { padding: [30, 30] });
    }

    // This map mounts inside a routed view (<Route path="devices" .../>),
    // so on first navigation here the container can still be settling its
    // layout when Leaflet reads its size. That stale size produces visible
    // seams/gridlines between tiles. A short delayed invalidateSize() forces
    // Leaflet to recheck the container's actual final size and redraw
    // cleanly once the route transition has settled.
    const sizeFixTimer = setTimeout(() => {
      leafletMap.current && leafletMap.current.invalidateSize();
    }, 100);
    return () => clearTimeout(sizeFixTimer);
  }, [data]);

  // Recalculate tile grid on window resize — same underlying cause as
  // above, but triggered by viewport or layout changes after initial
  // mount rather than at route-entry time.
  useEffect(() => {
    const handleResize = () => {
      leafletMap.current && leafletMap.current.invalidateSize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('en-GB', {
        day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', timeZone:'Europe/London',
      }) + ' GMT';
    } catch { return iso; }
  };

  if (loading) return <div style={{padding:48,textAlign:'center'}}><span className="spinner"/></div>;

  const allDevices = data?.devices || [];
  const archived   = allDevices.filter(d => d.archived);
  const unsorted   = showArchived ? archived : allDevices.filter(d => !d.archived);
  const devices    = [...unsorted].sort((a, b) => compareAgentDevices(a, b, sortKey, sortDir));
  const online     = allDevices.filter(d => d.status === 'online' && !d.archived).length;
  const offline    = allDevices.filter(d => d.status === 'offline' && !d.archived).length;
  const geoDevices = devices.filter(d => d.geo?.lat && d.geo?.lng);
  const hasDevices = data?.available && allDevices.filter(d => !d.archived).length > 0;

  return (
    <>
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      {showInstaller && <AgentInstallerPanel slug={slug} />}
      {!hasDevices ? (
        <div style={{padding:'8px 0',color:'var(--text-muted)',fontSize:13,textAlign:'center'}}>
          No devices enrolled yet. Generate an install command above to enroll your first device.
        </div>
      ) : (<>
      {/* Summary bar */}
      <div className="stat-tiles">{[
          {label:'Active devices', val:allDevices.filter(d=>!d.archived).length, tone:'accent'},
          {label:'Online',  val:online,  tone:'green'},
          {label:'Offline', val:offline, tone:offline>0?'red':'muted'},
        ].map(({label,val,tone})=>(
          <div key={label} className="stat-card">
            <div className={`stat-value ${tone}`}>{val}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
        {archived.length > 0 && (
          <button className="btn-card" aria-pressed={showArchived} onClick={()=>setShowArchived(s=>!s)} style={{minWidth:100,textAlign:'center'}}>
            <div style={{fontSize:22,fontWeight:800,color:'var(--text-muted)',fontFamily:'var(--font-mono)',lineHeight:1}}>{archived.length}</div>
            <div style={{fontSize:10,color:'var(--text-muted)',marginTop:4,textTransform:'uppercase',letterSpacing:'0.07em',fontWeight:600}}>{showArchived?'Back':'Archived'}</div>
          </button>
        )}
      </div>

      {/* Map */}
      {geoDevices.length > 0 && (
        <div style={{background:'var(--card-bg)',border:'1px solid var(--border)',borderRadius:10}}>
          <div style={{padding:'10px 14px',borderBottom:'1px solid var(--border)',fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em'}}>
            Device Locations · {geoDevices.length} device{geoDevices.length!==1?'s':''} geolocated
          </div>
          <div ref={mapRef} style={{height:280,width:'100%'}}/>
          {selected && (
            <div style={{padding:'12px 16px',borderTop:'1px solid var(--border)',display:'flex',gap:20,flexWrap:'wrap',background:'var(--surface)'}}>
              <div>
                <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:2}}>Device</div>
                <div style={{fontSize:13,fontWeight:600}}>{selected.display_name||selected.hostname||'Unknown'}</div>
              </div>
              <div>
                <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:2}}>Status</div>
                <AgentStatusBadge status={selected.status}/>
              </div>
              <div>
                <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:2}}>Location</div>
                <div style={{fontSize:12}}>{[selected.geo?.city, selected.geo?.country].filter(Boolean).join(', ')||'-'}</div>
              </div>
              <div>
                <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:2}}>ISP</div>
                <div style={{fontSize:12}}>{selected.geo?.isp||'-'}</div>
              </div>
              {selected.logged_in_user && (
                <div>
                  <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:2}}>Logged In</div>
                  <div style={{fontSize:12}}>{selected.logged_in_user}</div>
                </div>
              )}
              {selected.pending_reboot?.pending && (
                <div>
                  <div style={{fontSize:10,color:'#e6b800',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:2}}>Reboot Required</div>
                  <div style={{fontSize:12,color:'#e6b800'}}>{selected.pending_reboot.reason||'Yes'}</div>
                </div>
              )}
              <div>
                <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:2}}>Last Seen</div>
                <div style={{fontSize:12,fontFamily:'var(--font-mono)'}}>{formatDate(selected.last_seen)}</div>
              </div>
              <button className="btn btn-quiet btn-sm btn-icon" style={{ marginLeft: 'auto' }} onClick={()=>setSelected(null)}>✕</button>
            </div>
          )}
        </div>
      )}

      {/* Device list */}
      <div style={{background:'var(--card-bg)',border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
        <div style={{padding:'10px 14px',borderBottom:'1px solid var(--border)',fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
          <span>Enrolled Devices</span>
          <AgentSortBar sortKey={sortKey} sortDir={sortDir} onChange={(k,d)=>{setSortKey(k);setSortDir(d);}} />
        </div>
        {devices.map((dev, i) => (
          <div key={dev.device_id||i}
            style={{borderBottom:i<devices.length-1?'1px solid var(--border)':'none'}}>
            <div
              onClick={() => { setSelected(dev); setExpandedDevice(expandedDevice===dev.device_id?null:dev.device_id); }}
              style={{display:'flex',flexWrap:isMobile?'wrap':'nowrap',alignItems:isMobile?'flex-start':'center',gap:isMobile?8:12,padding:'10px 14px',
                cursor:'pointer',background:expandedDevice===dev.device_id?'rgba(0,212,255,0.04)':'transparent'}}>
            <div style={{width:8,height:8,borderRadius:'50%',flexShrink:0,
              background:dev.status==='online'?'#30d158':dev.status==='offline'?'#ff2d55':'#86868b'}}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,color:'var(--text)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',display:'flex',alignItems:'center',gap:6}}>
                <span style={{overflow:'hidden',textOverflow:'ellipsis'}}>{dev.display_name||dev.hostname||dev.device_id?.substring(0,8)||'Unknown'}</span>
                {dev.superseded_by && (
                  <span title={`Superseded by device ${dev.superseded_by}`}
                    style={{fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:4,background:'#86868b18',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',flexShrink:0}}>
                    Superseded
                  </span>
                )}
              </div>
              <div style={{fontSize:11,color:'var(--text-muted)'}}>
                {[dev.geo?.city, dev.geo?.country].filter(Boolean).join(', ')||'Location unknown'} · {dev.os_friendly||dev.os||''}
                {dev.agent_version && <span style={{marginLeft:6,opacity:0.6}}>{'- Agent v'+dev.agent_version}</span>}
              </div>
              <DeviceEnrichmentPills dev={dev} />
              {dev.last_compliance_check && (
                <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>
                  {'Last check: '}<span style={{fontFamily:'var(--font-mono)'}}>{formatDate(dev.last_compliance_check)}</span>
                  {dev.last_summary && (
                    <span style={{marginLeft:6,color:dev.last_summary.ce_plus?.grade==='pass'?'#30d158':dev.last_summary.ce_plus?.grade==='fail'?'#ff2d55':'var(--text-muted)',fontWeight:600}}>
                      {'CE+: '+dev.last_summary.ce_plus?.pct+'%'}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div style={isMobile?{flexBasis:'100%',display:'flex',flexWrap:'wrap',alignItems:'center',justifyContent:'space-between',gap:6,paddingLeft:20}:{flexShrink:0,textAlign:'right',display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4}}>
              <AgentStatusBadge status={dev.status}/>
              <div style={{fontSize:10,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>{formatDate(dev.last_seen)}</div>
              {isAdmin && !dev.archived && (
                <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:2}} onClick={e=>e.stopPropagation()}>
                  <button className="btn btn-secondary btn-sm" onClick={()=>{ setRenameModal(dev); setRenameValue(dev.display_name||''); }} disabled={actioning===dev.device_id}
                   >Rename</button>
                  <button className="btn btn-secondary btn-sm" onClick={()=>handleArchive(dev.device_id)} disabled={actioning===dev.device_id}
                   >Archive</button>
                  {confirming===dev.device_id ? (
                    <span style={{display:'flex',gap:4}}>
                      <button className="btn btn-danger btn-sm" onClick={()=>handleDelete(dev.device_id)} disabled={actioning===dev.device_id}
                       >Confirm</button>
                      <button className="btn btn-secondary btn-sm" onClick={()=>setConfirming(null)}
                       >Cancel</button>
                    </span>
                  ) : (
                    <button className="btn btn-danger btn-sm" onClick={()=>setConfirming(dev.device_id)}
                     >Delete</button>
                  )}
                </div>
              )}
              {isAdmin && dev.archived && (
                <div onClick={e=>e.stopPropagation()}>
                  <button className="btn btn-danger btn-sm" style={{ marginTop: 2 }} onClick={()=>handleDelete(dev.device_id)} disabled={actioning===dev.device_id}
                   >Delete</button>
                </div>
              )}
            </div>
            </div>
            {expandedDevice === dev.device_id && (
              <DeviceCardTabs slug={slug} dev={dev} isAdmin={isAdmin} />
            )}
            {dev.service_statuses && dev.service_statuses.length > 0 && (
              <ServiceStatusRow statuses={dev.service_statuses} />
            )}
          </div>
        ))}
      </div>
      </>)}
    </div>
    {renameModal && (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}
        onClick={()=>{ if (!renaming) { setRenameModal(null); setRenameValue(''); } }}>
        <div style={{background:'var(--bg)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:24,width:360,maxWidth:'90vw'}}
          onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>Rename Device</div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:16}}>
            {'Sets a display name for '}{renameModal.hostname||renameModal.device_id}{'. The reported hostname is unchanged.'}
          </div>
          <div style={{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.07em',color:'var(--text-muted)',marginBottom:6}}>Display Name</div>
          <input className="input-sm" type="text" value={renameValue} onChange={e=>setRenameValue(e.target.value)}
            placeholder={renameModal.hostname||'Enter a name…'}
            style={{ width:'100%', marginBottom:20 }}
            autoFocus
            onKeyDown={e=>{ if (e.key==='Enter') handleRename(); }} />
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <button className="btn btn-secondary btn-sm" onClick={()=>{ setRenameModal(null); setRenameValue(''); }} disabled={renaming}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleRename} disabled={renaming}>
              {renaming ? <span className="spinner"/> : 'Save'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function DeviceHub({ slug, isAdmin }) {
  const [subTab, setSubTab] = useState('network');
  const SUB_TABS = [
    { id: 'network',   label: 'Discovered Devices' },
    { id: 'computers', label: 'Managed Computers' },
    { id: 'agent',     label: 'Clearview Agent' },
  ];
  return (
    <HubPage title="Device Hub" subtitle="Discovered devices and managed computers">
      <SubTabBar tabs={SUB_TABS} active={subTab} onChange={setSubTab} />
      {subTab === 'network'   && <NetworkSubTab   slug={slug} isAdmin={isAdmin} />}
      {subTab === 'computers' && <ComputersSubTab slug={slug} isAdmin={isAdmin} />}
      {subTab === 'agent'     && <ClearviewAgentTab slug={slug} isAdmin={isAdmin} />}
    </HubPage>
  );
}

// ── Compliance Hub ────────────────────────────────────────────────────────────


// ── Compliance Framework Detail ───────────────────────────────────────────────

const RESULT_CONFIG = {
  compliant:     { label: 'Compliant',     color: '#30d158', short: '✓' },
  non_compliant: { label: 'Non-compliant', color: '#ff2d55', short: '✗' },
  not_assessed:  { label: 'Not assessed',  color: '#86868b', short: '○' },
};

const STATUS_CONFIG = {
  in_progress: { label: 'In progress', color: '#e6b800' },
  to_do:       { label: 'To do',       color: '#86868b' },
  done:        { label: 'Done',        color: '#30d158' },
};

function RequirementRow({ req }) {
  const [expanded, setExpanded] = useState(false);
  const rc = RESULT_CONFIG[req.result] || { label: req.result, color: '#86868b', short: '?' };
  const sc = STATUS_CONFIG[req.status]  || { label: req.status,  color: '#86868b' };
  const hasDetail = req.description || req.annotation;

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0',
        cursor: hasDetail ? 'pointer' : 'default',
      }} onClick={() => hasDetail && setExpanded(e => !e)}>
        <span style={{
          flexShrink: 0, width: 20, height: 20, borderRadius: 4, fontSize: 11,
          fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `${rc.color}18`, border: `1px solid ${rc.color}40`, color: rc.color,
        }}>{rc.short}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {req.ref_id && (
              <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', flexShrink: 0 }}>
                {req.ref_id}
              </span>
            )}
            <span style={{ fontSize: 12, fontWeight: req.result === 'non_compliant' ? 700 : 500 }}>
              {req.name || req.ref_id}
            </span>
          </div>
          {req.result === 'in_progress' || req.status === 'in_progress' ? (
            <span style={{ fontSize: 10, color: '#e6b800', marginTop: 2, display: 'block' }}>In progress</span>
          ) : null}
          {req.annotation && !expanded && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2,
              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '60ch' }}>
              {req.annotation}
            </div>
          )}
        </div>
        {hasDetail && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>
            {expanded ? '▲' : '▼'}
          </span>
        )}
      </div>
      {expanded && hasDetail && (
        <div style={{ padding: '0 0 12px 30px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {req.description && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              {req.description}
            </div>
          )}
          {req.annotation && (
            <div style={{
              fontSize: 12, padding: '8px 12px', borderRadius: 6,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderLeft: '3px solid var(--accent)',
            }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Evidence note
              </span>
              <div style={{ marginTop: 4, color: 'var(--text)' }}>{req.annotation}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DomainSection({ domain }) {
  const [expanded, setExpanded] = useState(domain.non_compliant > 0 || domain.in_progress > 0);
  const total = domain.requirements?.length || 0;
  const pct   = total > 0 ? Math.round((domain.compliant / total) * 100) : 0;
  const color = pct === 0 ? '#86868b' : domain.non_compliant > 0 ? '#ff2d55' : domain.in_progress > 0 ? '#e6b800' : pct < 100 ? '#e6b800' : '#30d158';

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
        cursor: 'pointer', background: 'var(--surface)',
      }} onClick={() => setExpanded(e => !e)}>
        <div style={{
          width: 36, height: 36, borderRadius: 6, flexShrink: 0,
          background: `${color}18`, border: `1px solid ${color}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 800, color, fontFamily: 'var(--font-mono)',
        }}>{pct}%</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{domain.label || domain.key}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {domain.compliant} compliant · {domain.non_compliant} non-compliant · {domain.not_assessed} not assessed · {total} total
          </div>
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>
          {expanded ? '▲' : '▼'}
        </span>
      </div>
      {expanded && (
        <div style={{ padding: '0 16px', background: 'var(--card-bg)' }}>
          {(domain.requirements || []).map((req, i) => (
            <RequirementRow key={i} req={req} />
          ))}
        </div>
      )}
    </div>
  );
}

function FrameworkDetail({ slug, frameworkKey, cisoUrl }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    authFetch(`/api/clients/${slug}/compliance`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}><span className="spinner" /></div>;
  if (!data?.available) return <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>No compliance data yet.</div>;

  const posture = data.data?.posture || {};
  // Find matching framework by key substring - handles CISO naming conventions
  // e.g. 'ISO/IEC 27001', 'General Data Protection Regulation', 'Cyber Essentials'
  const normKey = frameworkKey.toLowerCase().replace(/[^a-z0-9]/g, '');
  const fwKey   = Object.keys(posture).find(k => {
    const normK = k.toLowerCase().replace(/[^a-z0-9]/g, '');
    return normK.includes(normKey) || normKey.includes(normK) ||
      // Special aliases
      (normKey === 'gdpr' && normK.includes('generaldata')) ||
      (normKey === 'iso27001' && (normK.includes('iso') && normK.includes('27001'))) ||
      (normKey === 'cyberessentials' && normK.includes('cyber'));
  }) || frameworkKey;
  const fw      = posture[fwKey];
  const policies = (data.data?.policies || []);
  const risks    = data.data?.risk_summary || null;

  if (!fw || fw.not_configured) return (
    <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
      {fwKey} not yet configured in CISO Assistant.
    </div>
  );

  const reqs    = fw.requirements || {};
  const domains = fw.domains || [];
  const total   = reqs.total_assessable || 0;

  // Filter domains by result type
  const filteredDomains = filter === 'all' ? domains : domains.map(d => ({
    ...d,
    requirements: d.requirements.filter(r =>
      filter === 'non_compliant' ? r.result === 'non_compliant' :
      filter === 'in_progress'   ? r.status === 'in_progress' || r.result === 'in_progress' :
      filter === 'compliant'     ? r.result === 'compliant' :
      filter === 'not_assessed'  ? r.result === 'not_assessed' : true
    ),
  })).filter(d => d.requirements.length > 0);

  const FILTERS = [
    { id: 'all',           label: `All (${total})` },
    { id: 'non_compliant', label: `Non-compliant (${reqs.non_compliant || 0})`, color: '#ff2d55' },
    { id: 'in_progress',   label: `In progress (${reqs.in_progress || 0})`,     color: '#e6b800' },
    { id: 'compliant',     label: `Compliant (${reqs.compliant || 0})`,         color: '#30d158' },
    { id: 'not_assessed',  label: `Not assessed (${reqs.not_assessed || 0})`,   color: '#86868b' },
  ];

  return (
    <div>
      {/* Header stats */}
      <div className="stat-tiles">{[
          { label: 'Compliant',     val: reqs.compliant     || 0, tone:(reqs.compliant || 0) === 0 ? 'muted' : 'green' },
          { label: 'Non-compliant', val: reqs.non_compliant || 0, tone:'red' },
          { label: 'In progress',   val: reqs.in_progress   || 0, tone:'amber' },
          { label: 'Not assessed',  val: reqs.not_assessed  || 0, tone:'muted' },
          { label: 'Evidence items',val: fw.evidence_count  || 0, tone:'accent' },
        ].map(({label,val,tone})=>(
          <div key={label} className="stat-card">
            <div className={`stat-value ${tone}`}>{val}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
        {cisoUrl && (
          <a href={cisoUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" style={{ flexShrink: 0 }}>
            Open in CISO Portal →
          </a>
        )}
      </div>

      {/* Risk summary (ISO 27001) */}
      {risks && risks.total > 0 && frameworkKey === 'iso27001' && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '14px 16px', marginBottom: 16,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 10 }}>
            Risk Register
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div><span style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{risks.total}</span><span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>Total risks</span></div>
            <div><span style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-mono)', color: '#30d158' }}>{risks.treated}</span><span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>Treated</span></div>
            <div><span style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-mono)', color: '#ff2d55' }}>{risks.open}</span><span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>Open</span></div>
          </div>
        </div>
      )}

      {/* Policies */}
      {policies.length > 0 && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '14px 16px', marginBottom: 16,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 10 }}>
            Policies ({policies.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {policies.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{p.name}</div>
                  {p.description && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{p.description.slice(0, 80)}</div>}
                </div>
                {p.status && (
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3,
                    background: p.status === 'approved' ? '#30d15818' : 'var(--card-bg)',
                    border: `1px solid ${p.status === 'approved' ? '#30d15840' : 'var(--border)'}`,
                    color: p.status === 'approved' ? '#30d158' : 'var(--text-muted)',
                    flexShrink: 0,
                  }}>{p.status}</span>
                )}
                {p.link && (
                  <a href={p.link} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 11, color: 'var(--accent)', flexShrink: 0, textDecoration: 'none' }}>
                    View →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter bar */}
      {domains.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {FILTERS.map(f => (
              <button className="btn btn-secondary btn-sm" aria-pressed={filter === f.id} key={f.id} onClick={() => setFilter(f.id)}>{f.label}</button>
            ))}
          </div>

          {/* Domain sections */}
          <div>
            {filteredDomains.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                No requirements match this filter.
              </div>
            ) : (
              filteredDomains.map((domain, i) => <DomainSection key={i} domain={domain} />)
            )}
          </div>
        </>
      )}

      {domains.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          Requirement detail not yet available - trigger a compliance sync to populate.
        </div>
      )}
    </div>
  );
}


// ── Endpoint Assessment sub-tab ───────────────────────────────────────────────

const CONTROL_COLORS = {
  "Firewalls":"#00d4ff","Secure Configuration":"#bf5af2","User Access Control":"#0a84ff",
  "Malware Protection":"#30d158","Security Update Management":"#ff9f0a",
  "Clearview Baseline":"#5856d6","Supplemental":"#86868b",
};
const STATUS_COLOR = {pass:"#30d158",pass_assumed:"#e6b800",attested:"#30d158",fail:"#ff2d55",no_explicit_control_fail:"#ff2d55",no_data:"#86868b",informational:"#86868b",error:"#ff2d55"};
const STATUS_LABEL = {pass:"Pass",pass_assumed:"Pass*",attested:"Verified",fail:"Fail",no_explicit_control_fail:"Manual check required",no_data:"No data",informational:"Info",error:"Check Error"};

function EndpointSummaryBar({ summary }) {
  const ce = summary?.ce_plus || {};
  const bl = summary?.clearview_baseline || {};
  const gradeColor = ce.grade==="pass"?"#30d158":ce.grade==="fail"?"#ff2d55":"#86868b";
  return (
    <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:20}}>
      <div style={{background:"var(--card-bg)",border:`1px solid var(--border)`,borderTop:`3px solid ${gradeColor}`,borderRadius:10,padding:"14px 18px",minWidth:140}}>
        <div style={{fontSize:28,fontWeight:800,color:gradeColor,fontFamily:"var(--font-mono)",lineHeight:1}}>{ce.pct??0}%</div>
        <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:600}}>Cyber Essentials Plus</div>
        <div style={{fontSize:11,fontWeight:700,color:gradeColor,marginTop:4}}>{ce.grade==="pass"?"PASS":ce.grade==="fail"?"FAIL":"-"}{ce.auto_fails>0?` · ${ce.auto_fails} auto-fail`:""}</div>
      </div>
      <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderTop:"3px solid #5856d6",borderRadius:10,padding:"14px 18px",minWidth:140}}>
        <div style={{fontSize:28,fontWeight:800,color:"#5856d6",fontFamily:"var(--font-mono)",lineHeight:1}}>{bl.pct??0}%</div>
        <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:600}}>Clearview Baseline</div>
        <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>{bl.pass??0} of {bl.total??0} passed</div>
      </div>
      {[{label:"Pass",val:ce.pass,color:"#30d158"},{label:"Fail",val:ce.fail,color:"#ff2d55"},{label:"Auto-Fail",val:ce.auto_fails,color:"#ff2d55"}].map(({label,val,color})=>(
        <div key={label} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,padding:"14px 18px",minWidth:90,textAlign:"center"}}>
          <div style={{fontSize:24,fontWeight:800,color:val>0?color:"var(--text-muted)",fontFamily:"var(--font-mono)",lineHeight:1}}>{val??0}</div>
          <div style={{fontSize:10,color:"var(--text-muted)",marginTop:4,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:600}}>{label}</div>
        </div>
      ))}
    </div>
  );
}

function EndpointResultsTable({ results, filterLayer, isAdmin, attestations, onAttest }) {
  const filtered = results.filter(r => filterLayer==="all"||r.layer===filterLayer);
  const grouped = {};
  filtered.forEach(r=>{const c=r.control||"Other";if(!grouped[c])grouped[c]=[];grouped[c].push(r);});
  const canAttest = isAdmin;
  const attestable = s => s==="no_explicit_control_fail"||s==="error";
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {Object.entries(grouped).map(([control,items])=>(
        <div key={control}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <div style={{width:10,height:10,borderRadius:"50%",background:CONTROL_COLORS[control]||"#86868b",flexShrink:0}}/>
            <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",color:"var(--text-muted)"}}>{control}</div>
          </div>
          <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,overflow:"hidden"}}>
            {items.map((r,i)=>{
              const sc=STATUS_COLOR[r.status]||"#86868b";
              const rowBg=r.status==="fail"||r.status==="no_explicit_control_fail"?"rgba(255,45,85,0.04)":r.status==="pass_assumed"?"rgba(230,184,0,0.04)":r.status==="error"?"rgba(255,45,85,0.04)":"transparent";
              const isAttested=r.status==="attested";
              const needsAttestation=attestable(r.status)||(canAttest&&attestations&&attestations[r.query_id]&&!isAttested);
              return (
                <div key={r.query_id} style={{display:"flex",alignItems:"flex-start",gap:12,padding:"12px 16px",borderBottom:i<items.length-1?"1px solid var(--border)":"none",background:isAttested?"rgba(48,209,88,0.04)":rowBg}}>
                  <div style={{flexShrink:0,marginTop:2}}>
                    <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4,background:`${sc}18`,border:`1px solid ${sc}40`,color:sc,whiteSpace:"nowrap",fontFamily:"var(--font-mono)"}}>
                      {STATUS_LABEL[r.status]||r.status}{r.auto_fail&&r.status==="fail"?" ⚠":""}
                    </span>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--text)",marginBottom:2}}>{r.requirement}</div>
                    {r.status==="fail"&&<div style={{fontSize:11,color:"#ff2d55"}}>{r.fail_label}</div>}
                    {r.status==="pass_assumed"&&<div style={{fontSize:11,color:"#e6b800"}}>{'No Explicit Control - '+(r.no_data_note||'Default behaviour assumed safe. Manual verification recommended.')}</div>}
                    {r.status==="no_explicit_control_fail"&&<div style={{fontSize:11,color:"#ff2d55"}}>{r.no_data_note||'Cannot be verified automatically. Admin verification required.'}</div>}
                    {r.status==="error"&&<div style={{fontSize:11,color:"#ff2d55"}}>Check failed to run — verify agent permissions or query compatibility.</div>}
                    {r.status==="no_data"&&<div style={{fontSize:11,color:"var(--text-muted)"}}>No data returned - check agent permissions or registry key may not exist.</div>}
                    {isAttested&&<div style={{fontSize:11,color:"#30d158"}}>Verified by {r.attested_by||"admin"}{r.attested_at?" on "+new Date(r.attested_at).toLocaleString("en-GB",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit",timeZone:"Europe/London"})+" GMT":""}</div>}
                    {canAttest&&attestable(r.status)&&(
                      <label style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:6,cursor:"pointer"}}>
                        <input type="checkbox" checked={false} onChange={e=>{if(e.target.checked&&onAttest)onAttest(r.query_id,true);}} style={{accentColor:"#30d158",width:13,height:13,cursor:"pointer"}}/>
                        <span style={{fontSize:11,color:"var(--text-muted)"}}>Mark as verified — I have manually confirmed this control is in place</span>
                      </label>
                    )}
                    {canAttest&&isAttested&&(
                      <label style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:6,cursor:"pointer"}}>
                        <input type="checkbox" checked={true} onChange={e=>{if(!e.target.checked&&onAttest)onAttest(r.query_id,false);}} style={{accentColor:"#30d158",width:13,height:13,cursor:"pointer"}}/>
                        <span style={{fontSize:11,color:"#30d158"}}>Verified by admin — untick to remove</span>
                      </label>
                    )}
                  </div>
                  <div style={{flexShrink:0,fontSize:10,color:"var(--text-muted)",fontFamily:"var(--font-mono)",whiteSpace:"nowrap"}}>{r.layer==="Clearview Baseline"?"Baseline":"CE+"}</div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function EndpointDeviceCard({ device, isSelected, onClick }) {
  const ce = device.summary?.ce_plus||{};
  const gradeColor = ce.grade==="pass"?"#30d158":ce.grade==="fail"?"#ff2d55":"#86868b";
  return (
    <div onClick={onClick} style={{background:isSelected?"rgba(0,212,255,0.07)":"var(--card-bg)",border:`1px solid ${isSelected?"var(--accent)":"var(--border)"}`,borderRadius:8,padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
      <div style={{width:8,height:8,borderRadius:"50%",background:gradeColor,flexShrink:0}}/>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:13,fontWeight:600,color:isSelected?"var(--accent)":"var(--text)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{device.device?.hostname||device.device?.uuid?.substring(0,8)||"Unknown"}</div>
        <div style={{fontSize:11,color:"var(--text-muted)"}}>{device.device?.model||""}</div>
      </div>
      <div style={{fontSize:12,fontWeight:700,color:gradeColor,fontFamily:"var(--font-mono)",flexShrink:0}}>{ce.pct??0}%</div>
    </div>
  );
}

function EndpointAssessmentTab({ slug, isAdmin }) {
  const [data,setData]           = useState(null);
  const [loading,setLoading]     = useState(true);
  const [attestations,setAttestations] = useState({});
  const [selectedDevice,setSelectedDevice] = useState(0);
  const [filterLayer,setFilterLayer]       = useState("all");

  const loadData = () => {
    if(!slug)return;
    setLoading(true);
    authFetch(`/api/clients/${slug}/endpoint`).then(r=>r.json()).then(d=>{setData(d);setLoading(false);}).catch(()=>setLoading(false));
  };

  useEffect(()=>{
    loadData();
    if(isAdmin){
      authFetch(`/api/clients/${slug}/endpoint/attestations`).then(r=>r.json()).then(d=>setAttestations(d.attestations||{})).catch(()=>{});
    }
  },[slug,isAdmin]);

  const handleAttest = async (queryId, attested) => {
    try {
      const r = await authFetch(`/api/clients/${slug}/endpoint/attestations`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({query_id:queryId,attested}),
      });
      if(r.ok){ loadData(); authFetch(`/api/clients/${slug}/endpoint/attestations`).then(r=>r.json()).then(d=>setAttestations(d.attestations||{})).catch(()=>{}); }
    } catch(e){}
  };

  if(loading)return <div style={{padding:48,textAlign:"center"}}><span className="spinner"/></div>;
  if(!data?.available||!data?.data?.devices?.length)return(
    <div style={{padding:32,textAlign:"center",color:"var(--text-muted)",fontSize:13}}>
      No endpoint assessment data yet. Deploy the STG Endpoint Agent to start collecting data.
    </div>
  );

  const devices=data.data.devices;
  const device=devices[selectedDevice]||devices[0];
  const results=device.results||[];
  const layerCounts=results.reduce((acc,r)=>{acc[r.layer]=(acc[r.layer]||0)+1;return acc;},{});

  return (
    <div style={{display:"flex",gap:16,alignItems:"flex-start"}}>
      {devices.length>1&&(
        <div style={{width:220,flexShrink:0,display:"flex",flexDirection:"column",gap:6}}>
          <div style={{fontSize:11,fontWeight:600,letterSpacing:"0.07em",textTransform:"uppercase",color:"var(--text-muted)",marginBottom:4}}>{devices.length} Device{devices.length!==1?"s":""}</div>
          {devices.map((d,i)=><EndpointDeviceCard key={d.device?.uuid||i} device={d} isSelected={i===selectedDevice} onClick={()=>setSelectedDevice(i)}/>)}
        </div>
      )}
      <div style={{flex:1,minWidth:0}}>
        <div style={{marginBottom:16}}>
          <div style={{fontSize:15,fontWeight:700,color:"var(--text)"}}>{device.device?.hostname||"Unknown Device"}</div>
          <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>
            {device.device?.model&&<span>{device.device.model} · </span>}
            {device.device?.serial&&<span>S/N {device.device.serial} · </span>}
            {device.collected_at&&<span>Assessed {new Date(device.collected_at).toLocaleString("en-GB",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit",timeZone:"Europe/London"})} GMT</span>}
          </div>
        </div>
        <EndpointSummaryBar summary={device.summary}/>
        <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
          {[{id:"all",label:`All (${results.length})`},{id:"Cyber Essentials Plus",label:`CE+ (${layerCounts["Cyber Essentials Plus"]||0})`},{id:"Clearview Baseline",label:`Clearview Baseline (${layerCounts["Clearview Baseline"]||0})`}].map(f=>(
            <button className="btn btn-secondary btn-sm" aria-pressed={filterLayer===f.id} key={f.id} onClick={()=>setFilterLayer(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        <EndpointResultsTable results={results} filterLayer={filterLayer} isAdmin={isAdmin} attestations={attestations} onAttest={handleAttest}/>
      </div>
    </div>
  );
}

const COMPLIANCE_TABS = ['overview', 'cyber-essentials', 'gdpr', 'iso27001', 'endpoint', 'phishing'];

function ComplianceHub({ slug, meta, isAdmin }) {
  const [subTab, setSubTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return COMPLIANCE_TABS.includes(t) ? t : 'overview';
  });
  const [compliance, setCompliance] = useState(null);

  useEffect(() => {
    authFetch(`/api/clients/${slug}/compliance`)
      .then(r => r.json())
      .then(d => setCompliance(d))
      .catch(() => {});
  }, [slug]);

  // Build CISO portal URL from edge heartbeat data
  const edgeId    = compliance?.data?.posture ? Object.values(compliance.data.posture)[0]?.assessment_id : null;
  const cisoUrlBase = meta?.ciso_url || null;

  const SUB_TABS = [
    { id: 'overview',          label: 'Overview' },
    { id: 'cyber-essentials',  label: 'Cyber Essentials' },
    { id: 'gdpr',              label: 'GDPR' },
    { id: 'iso27001',          label: 'ISO 27001' },
    { id: 'endpoint',          label: 'Endpoint Assessment' },
    { id: 'phishing',          label: 'Phishing Simulation' },
  ];

  return (
    <HubPage title="Compliance Hub" subtitle="Compliance frameworks, requirements and evidence">
      <SubTabBar tabs={SUB_TABS} active={subTab} onChange={setSubTab} />
      {subTab === 'overview'         && <ComplianceTile slug={slug} />}
      {subTab === 'cyber-essentials' && <FrameworkDetail slug={slug} frameworkKey="cyber essentials" cisoUrl={cisoUrlBase} />}
      {subTab === 'gdpr'             && <FrameworkDetail slug={slug} frameworkKey="gdpr" cisoUrl={cisoUrlBase} />}
      {subTab === 'iso27001'         && <FrameworkDetail slug={slug} frameworkKey="iso 27001" cisoUrl={cisoUrlBase} />}
      {subTab === 'endpoint'         && <EndpointAssessmentTab slug={slug} isAdmin={isAdmin} />}
      {subTab === 'phishing'         && <PhishingSection slug={slug} isAdmin={isAdmin} />}
    </HubPage>
  );
}

// ── Productivity Hub ──────────────────────────────────────────────────────────

function TrustM365DriftDetail({ slug }) {
  const [drift, setDrift] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch(`/api/m365/drift/${slug}`)
      .then(r => r.json())
      .then(d => { setDrift(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div style={{padding:'12px 0'}}><span className="spinner"/></div>;
  if (!drift?.available) return (
    <div style={{padding:'16px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,fontSize:13,color:'var(--text-muted)'}}>
      No M365 config drift data available. Add a tenant UUID in Client Settings to enable M365 config drift monitoring.
    </div>
  );

  const statusColor = drift.overall_status === 'drifted' ? '#ff2d55'
    : drift.overall_status === 'clean' ? '#30d158'
    : drift.overall_status === 'partial' ? '#e6b800'
    : 'var(--text-muted)';

  const statusLabel = drift.overall_status === 'drifted' ? 'Drift Detected'
    : drift.overall_status === 'clean' ? 'All Clean'
    : drift.overall_status === 'partial' ? 'Partially Monitored'
    : drift.overall_status === 'unconfigured' ? 'No Baseline Set'
    : drift.overall_status;

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
        <span style={{fontSize:12,fontWeight:700,color:statusColor,background:`${statusColor}18`,border:`1px solid ${statusColor}40`,borderRadius:4,padding:'3px 10px'}}>{statusLabel}</span>
        {drift.drifted_areas > 0 && <span style={{fontSize:13,color:'var(--text-muted)'}}>{drift.total_drifts} change{drift.total_drifts!==1?'s':''} across {drift.drifted_areas} area{drift.drifted_areas!==1?'s':''}</span>}
        {drift.polled_at && <span style={{fontSize:12,color:'var(--text-muted)',marginLeft:'auto'}}>Updated {new Date(drift.polled_at).toLocaleString('en-GB',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})} GMT</span>}
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        {(drift.areas||[]).map(area => {
          const hasDrift = area.drift?.status === 'drifted' && (area.drift?.drift_count||0) > 0;
          const isClean  = area.drift?.status === 'clean' || (area.drift?.status === 'drifted' && (area.drift?.drift_count||0) === 0);
          const noBaseline = !area.hasBaseline;
          const dotColor = hasDrift ? '#ff2d55' : isClean ? '#30d158' : noBaseline ? 'var(--text-muted)' : '#e6b800';
          return (
            <div key={area.areaKey} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8}}>
              <span style={{color:dotColor,fontSize:10,flexShrink:0}}>●</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{area.displayName}</div>
                {hasDrift && area.drift?.drift_count > 0 && (
                  <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>{area.drift.drift_count} change{area.drift.drift_count!==1?'s':''}</div>
                )}
              </div>
              <div style={{textAlign:'right',flexShrink:0}}>
                {noBaseline
                  ? <span style={{fontSize:11,color:'var(--text-muted)'}}>No baseline</span>
                  : hasDrift
                    ? <span style={{fontSize:11,fontWeight:600,color:'#ff2d55'}}>Drifted</span>
                    : isClean
                      ? <span style={{fontSize:11,color:'#30d158'}}>Clean</span>
                      : <span style={{fontSize:11,color:'#e6b800'}}>Pending</span>
                }
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProductivityHubWrapper({ slug }) {
  const [subTab, setSubTab] = useState('m365');
  const SUB_TABS = [
    { id: 'm365',     label: 'Microsoft 365' },
    { id: 'drift',    label: 'Config Drift' },
  ];
  return (
    <HubPage title="Productivity Hub" subtitle="Microsoft 365 and connected productivity services">
      <SubTabBar tabs={SUB_TABS} active={subTab} onChange={setSubTab} />
      {subTab === 'm365'  && <ProductivityHub slug={slug} />}
      {subTab === 'drift' && <TrustM365DriftDetail slug={slug} />}
    </HubPage>
  );
}

// ── Reports Hub ───────────────────────────────────────────────────────────────

function ReportsHub({ slug, meta, isAdmin }) {
  const [reports, setReports]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [downloading, setDownloading]     = useState('');
  const [deleting, setDeleting]           = useState('');
  const [showInternal, setShowInternal]   = useState(false);
  const [assessments, setAssessments]     = useState([]);
  const [assessLoading, setAssessLoading] = useState(true);
  const [gradeHistory, setGradeHistory]   = useState(null);


  const loadAssessments = () => {
    authFetch(`/api/clients/${slug}/assessment-reports${isAdmin ? '' : '?as_client=1'}`)
      .then(r => r.json())
      .then(d => { setAssessments((Array.isArray(d) ? d : []).filter(r => isAdmin || !r.is_internal)); setAssessLoading(false); })
      .catch(() => setAssessLoading(false));
  };

  useEffect(() => {
    authFetch(`/api/clients/${slug}/reports`).then(r=>r.json()).then(d=>{setReports((Array.isArray(d)?d:[]).filter(r=>isAdmin||!r.is_internal));setLoading(false);}).catch(()=>setLoading(false));
  }, [slug, isAdmin]);

  useEffect(() => { loadAssessments(); }, [slug, isAdmin]);

  useEffect(() => {
    authFetch(`/api/clients/${slug}/assessment-history`)
      .then(r => r.json())
      .then(d => setGradeHistory(d && Array.isArray(d.history) ? d : null))
      .catch(() => {});
  }, [slug]);

  const handleDownload = async (filename) => {
    setDownloading(filename);
    try{const token=localStorage.getItem('stg_token');const res=await fetch(`/api/clients/${slug}/reports/${filename}`,{headers:{Authorization:`Bearer ${token}`}});if(!res.ok)throw new Error('Download failed');const blob=await res.blob();const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download=filename;a.click();URL.revokeObjectURL(url);}
    catch(err){alert('Download failed: '+err.message);}finally{setDownloading('');}
  };

  const handleDelete = async (filename) => {
    if(!window.confirm(`Delete "${filename}"?\n\nThis will also delete the paired internal report. This cannot be undone.`))return;
    setDeleting(filename);
    try{const res=await authFetch(`/api/clients/${slug}/reports/${encodeURIComponent(filename)}`,{method:'DELETE'});const d=await res.json();if(!res.ok){alert(d.error||'Delete failed');return;}
      authFetch(`/api/clients/${slug}/reports`).then(r=>r.json()).then(data=>{if(Array.isArray(data))setReports(data.filter(r=>isAdmin||!r.is_internal));}).catch(()=>{});
    }catch(err){alert(err.message);}finally{setDeleting(null);}
  };

  const handleView = (filename) => {
    const token=localStorage.getItem('stg_token');
    window.open(`/api/clients/${slug}/reports/${filename}/view?token=${token}`,'_blank');
  };

  const handleAssessView = (filename) => {
    window.open(`/api/clients/${slug}/assessment-reports/${encodeURIComponent(filename)}?view=1`,'_blank');
  };
  const handleAssessDownload = async (filename) => {
    setDownloading(filename);
    try{const token=localStorage.getItem('stg_token');const res=await fetch(`/api/clients/${slug}/assessment-reports/${encodeURIComponent(filename)}`,{headers:{Authorization:`Bearer ${token}`}});if(!res.ok)throw new Error('Download failed');const blob=await res.blob();const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download=filename;a.click();URL.revokeObjectURL(url);}
    catch(err){alert('Download failed: '+err.message);}finally{setDownloading('');}
  };
  const handleAssessDelete = async (filename) => {
    if(!window.confirm(`Delete "${filename}"?\n\nThis also removes the paired internal report. This cannot be undone.`))return;
    setDeleting(filename);
    try{const res=await authFetch(`/api/clients/${slug}/assessment-reports/${encodeURIComponent(filename)}`,{method:'DELETE'});const d=await res.json();if(!res.ok){alert(d.error||'Delete failed');return;}loadAssessments();}
    catch(err){alert(err.message);}finally{setDeleting('');}
  };
  const visible         = reports.filter(r=>showInternal||!r.is_internal);
  const clientReports   = visible.filter(r=>!r.is_internal);
  const internalReports = visible.filter(r=>r.is_internal);

  return (
    <HubPage title="Reports" subtitle="IT and Cyber Security Reports, External Cyber Security Assessments, Website Analytics Reports and grade history">
      {/* Toolbar */}
      <div className="cd-toolbar" style={{marginBottom:16}}>
        <div className="cd-toolbar-left">
          <span className="tag tag-muted">{reports.length} report{reports.length!==1?'s':''} total</span>
        </div>
        <div className="cd-toolbar-right">
          {isAdmin&&(<label className="toggle-label"><input type="checkbox" checked={showInternal} onChange={e=>setShowInternal(e.target.checked)}/>Show internal reports</label>)}
          {isAdmin&&<ReportActions slug={slug} kind="it" />}
        </div>
      </div>

      {/* Client details */}
      {isAdmin&&(meta?.contact||meta?.contact_email||meta?.notes||meta?.domains||meta?.sector?.length||meta?.company_number)&&(
        <div className="card cd-meta-card" style={{marginTop:16}}>
          <div style={{fontSize:12,fontWeight:600,letterSpacing:'0.07em',textTransform:'uppercase',color:'var(--text-muted)',marginBottom:12}}>Client Details</div>
          <div className="cd-meta-grid">
            {meta.domains&&<div><div className="cd-meta-label">Domains</div><div className="cd-meta-value">{meta.domains}</div></div>}
            {(Array.isArray(meta.sector)?meta.sector.length>0:!!meta.sector)&&<div><div className="cd-meta-label">Sector</div><div className="cd-meta-value">{Array.isArray(meta.sector)?meta.sector.join(', '):meta.sector}</div></div>}
            {meta.company_number&&<div><div className="cd-meta-label">Companies House Number</div><div className="cd-meta-value"><a href={`https://find-and-update.company-information.service.gov.uk/company/${meta.company_number}`} target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)'}}>{meta.company_number} ↗</a></div></div>}
            {meta.contact&&<div><div className="cd-meta-label">Contact</div><div className="cd-meta-value">{meta.contact}</div></div>}
            {meta.contact_email&&<div><div className="cd-meta-label">Email</div><div className="cd-meta-value">{meta.contact_email}</div></div>}
            {meta.notes&&<div style={{gridColumn:'1/-1'}}><div className="cd-meta-label">Notes</div><div className="cd-meta-value">{meta.notes}</div></div>}
          </div>
        </div>
      )}

      {/* Report list */}
      {loading?(<div style={{padding:48,textAlign:'center'}}><span className="spinner"/></div>
      ):reports.length===0?(<div className="cd-empty" style={{marginTop:16}}>No reports found for this client.</div>
      ):(
        <div style={{marginTop:16}}>
          {clientReports.length>0&&(
            <div className="cd-section">
              <div className="cd-section-title">IT and Cyber Security Reports</div>
              <div className="cd-list card">
                {clientReports.map(r=>(
                  <div key={r.filename} className="cd-item">
                    <div className="cd-item-left">
                      <GradeBadge grade={r.grade}/>
                      <div>
                        <div className="cd-filename">{r.filename}</div>
                        <div className="cd-meta">{formatDate(r.date)} · {formatSize(r.size)}</div>
                      </div>
                    </div>
                    <div className="cd-item-actions">
                      <button className="btn btn-secondary btn-sm cd-btn" onClick={()=>handleView(r.filename)}>View</button>
                      <button className="btn btn-secondary btn-sm cd-btn" onClick={()=>handleDownload(r.filename)} disabled={downloading===r.filename}>{downloading===r.filename?<span className="spinner"/>:'↓ Download'}</button>
                      {isAdmin&&<button className="btn btn-danger btn-sm cd-btn" onClick={()=>handleDelete(r.filename)} disabled={deleting===r.filename}>{deleting===r.filename?<span className="spinner"/>:'Delete'}</button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {showInternal&&internalReports.length>0&&(
            <div className="cd-section">
              <div className="cd-section-title">Internal Reports <span className="tag tag-amber" style={{marginLeft:10,fontSize:10}}>STG EYES ONLY</span></div>
              <div className="cd-list card">
                {internalReports.map(r=>(
                  <div key={r.filename} className="cd-item">
                    <div className="cd-item-left">
                      <span className="tag tag-amber" style={{fontSize:11,padding:'4px 8px'}}>INT</span>
                      <div><div className="cd-filename">{r.filename}</div><div className="cd-meta">{formatDate(r.date)} · {formatSize(r.size)}</div></div>
                    </div>
                    <div className="cd-item-actions">
                      <button className="btn btn-secondary btn-sm cd-btn" onClick={()=>handleView(r.filename)}>View</button>
                      <button className="btn btn-secondary btn-sm cd-btn" onClick={()=>handleDownload(r.filename)} disabled={downloading===r.filename}>{downloading===r.filename?<span className="spinner"/>:'↓ Download'}</button>
                      {isAdmin&&<button className="btn btn-danger btn-sm cd-btn" onClick={()=>handleDelete(r.filename)} disabled={deleting===r.filename}>{deleting===r.filename?<span className="spinner"/>:'Delete'}</button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* External assessments */}
      <div className="cd-section" style={{marginTop:24}}>
        <div className="cd-toolbar" style={{marginBottom:12}}>
          <div className="cd-toolbar-left">
            <span className="cd-section-title" style={{margin:0}}>External Cyber Security Assessments</span>
          </div>
          <div className="cd-toolbar-right">
            {isAdmin&&<button className="btn btn-secondary" onClick={loadAssessments}>⟳ Refresh</button>}
            {isAdmin&&<ReportActions slug={slug} kind="assessment" />}
          </div>
        </div>
        {gradeHistory&&gradeHistory.history.length>=2&&(()=>{
          const h=gradeHistory.history;
          const first=gradeHistory.first||h[0], latest=gradeHistory.latest||h[h.length-1];
          const rank=g=>'FEDCBA'.indexOf((g&&g[0])||'?');
          const delta=rank(latest.grade)-rank(first.grade);
          const dir=delta>0?{t:'improved',c:'#30d158',a:'↑'}:delta<0?{t:'declined',c:'#ff453a',a:'↓'}:{t:'unchanged',c:'var(--text-muted)',a:'→'};
          const fmt=d=>{try{return new Date(d).toLocaleDateString('en-GB',{month:'short',year:'numeric'});}catch(e){return '';}};
          return (
            <div className="card" style={{marginBottom:12,padding:'14px 16px'}}>
              <div style={{fontSize:11,fontWeight:600,letterSpacing:'0.07em',textTransform:'uppercase',color:'var(--text-muted)',marginBottom:10}}>Grade over time</div>
              <div style={{display:'flex',alignItems:'center',gap:14,flexWrap:'wrap'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{textAlign:'center'}}><GradeBadge grade={first.grade}/><div style={{fontSize:10,color:'var(--text-muted)',marginTop:3}}>{fmt(first.date)}</div></div>
                  <span style={{fontSize:20,color:dir.c,fontWeight:700}}>{dir.a}</span>
                  <div style={{textAlign:'center'}}><GradeBadge grade={latest.grade}/><div style={{fontSize:10,color:'var(--text-muted)',marginTop:3}}>{fmt(latest.date)}</div></div>
                </div>
                <div style={{fontSize:13,color:dir.c,fontWeight:600}}>
                  Security {dir.t}
                  {latest.score!=null&&first.score!=null?` — ${first.score} to ${latest.score} of 100`:''}
                </div>
                <div style={{marginLeft:'auto',display:'flex',gap:5,alignItems:'center'}}>
                  {h.slice(-8).map((x,i)=>(<span key={i} title={`${x.grade||'?'} · ${fmt(x.date)}`}><GradeBadge grade={x.grade}/></span>))}
                </div>
              </div>
            </div>
          );
        })()}
        {assessLoading?(<div style={{padding:32,textAlign:'center'}}><span className="spinner"/></div>
        ):assessments.length===0?(<div className="cd-empty">No assessments yet.{isAdmin?' Run one to scan this client\'s external security.':''}</div>
        ):(
          <div className="cd-list card">
            {assessments.map(r=>(
              <div key={r.filename} className="cd-item">
                <div className="cd-item-left">
                  {r.is_internal?<span className="tag tag-amber" style={{fontSize:11,padding:'4px 8px'}}>INT</span>:<GradeBadge grade={r.grade}/>}
                  <div>
                    <div className="cd-filename">{r.filename}</div>
                    <div className="cd-meta">{formatDate(r.date)} · {formatSize(r.size)}{r.is_internal?' · STG eyes only':''}</div>
                  </div>
                </div>
                <div className="cd-item-actions">
                  <button className="btn btn-secondary btn-sm cd-btn" onClick={()=>handleAssessView(r.filename)}>View</button>
                  <button className="btn btn-secondary btn-sm cd-btn" onClick={()=>handleAssessDownload(r.filename)} disabled={downloading===r.filename}>{downloading===r.filename?<span className="spinner"/>:'↓ Download'}</button>
                  {isAdmin&&<button className="btn btn-danger btn-sm cd-btn" onClick={()=>handleAssessDelete(r.filename)} disabled={deleting===r.filename}>{deleting===r.filename?<span className="spinner"/>:'Delete'}</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AnalyticsReportsSection slug={slug} isAdmin={isAdmin}/>
    </HubPage>
  );
}

// v2.98: saved Website Analytics Reports (app.py v3.96)
function AnalyticsReportsSection({ slug, isAdmin }) {
  const [items, setItems]             = useState(null);
  const [downloading, setDownloading] = useState('');
  const [deleting, setDeleting]       = useState('');
  const load = () => {
    authFetch(`/api/clients/${slug}/analytics-reports`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]));
  };
  useEffect(load, [slug]);

  const view = (f) => window.open(`/api/clients/${slug}/analytics-reports/${encodeURIComponent(f)}?view=1`, '_blank');
  const download = async (f) => {
    setDownloading(f);
    try {
      const res = await authFetch(`/api/clients/${slug}/analytics-reports/${encodeURIComponent(f)}`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = f; a.click(); URL.revokeObjectURL(url);
    } catch (err) { alert('Download failed: ' + err.message); } finally { setDownloading(''); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete "${f}"?\n\nThis removes the saved copy only; the email already sent is not affected. This cannot be undone.`)) return;
    setDeleting(f);
    try {
      const res = await authFetch(`/api/clients/${slug}/analytics-reports/${encodeURIComponent(f)}`, { method: 'DELETE' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { alert(d.error || 'Delete failed'); return; }
      load();
    } catch (err) { alert(err.message); } finally { setDeleting(''); }
  };

  if (items === null) return null;
  return (
    <div className="cd-section" style={{marginTop:24}}>
      <div className="cd-toolbar" style={{marginBottom:12}}>
        <div className="cd-toolbar-left"><span className="cd-section-title" style={{margin:0}}>Website Analytics Reports</span></div>
        <div className="cd-toolbar-right">{isAdmin && <ReportActions slug={slug} kind="analytics" onSent={load} />}</div>
      </div>
      {items.length === 0 ? (
        <div className="cd-empty">No website analytics reports yet.</div>
      ) : (
        <div className="cd-list card">
          {items.map(r => {
            const stats = [r.period, r.visits != null ? `${r.visits} visit${String(r.visits) === '1' ? '' : 's'}` : '', r.enquiries != null ? `${r.enquiries} enquir${String(r.enquiries) === '1' ? 'y' : 'ies'}` : ''].filter(Boolean).join(' · ');
            return (
              <div key={r.filename} className="cd-item">
                <div className="cd-item-left">
                  <span className="tag" style={{fontSize:11,padding:'4px 8px'}}>WEB</span>
                  <div>
                    <div className="cd-filename">{r.subject || r.filename}</div>
                    <div className="cd-meta">{formatDate(r.date)}{stats ? ` · ${stats}` : ''}</div>
                  </div>
                </div>
                <div className="cd-item-actions">
                  <button className="btn btn-secondary btn-sm cd-btn" onClick={() => view(r.filename)}>View</button>
                  <button className="btn btn-secondary btn-sm cd-btn" onClick={() => download(r.filename)} disabled={downloading === r.filename}>{downloading === r.filename ? <span className="spinner"/> : '↓ Download'}</button>
                  {isAdmin && <button className="btn btn-danger btn-sm cd-btn" onClick={() => remove(r.filename)} disabled={deleting === r.filename}>{deleting === r.filename ? <span className="spinner"/> : 'Delete'}</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Tickets Hub ───────────────────────────────────────────────────────────────

function TicketsHub({ slug, user }) {
  return <Tickets user={user} hideHeader />;
}

// ── Assets Hub ────────────────────────────────────────────────────────────────

function AssetsHub({ slug, isAdmin }) {
  return (
    <HubPage title="Assets Hub" subtitle="Licences, contracts, devices, domains and all tracked assets">
      <AssetHub slug={slug} isAdmin={isAdmin} showClient={false} />
    </HubPage>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

// ── Hub disabled placeholder ──────────────────────────────────────────────────
function HubDisabled({ hubName }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '60px 24px', gap: 12, color: 'var(--text-muted)', textAlign: 'center',
    }}>
      <i className="ti ti-lock" style={{ fontSize: 36, opacity: 0.4 }} />
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', opacity: 0.6 }}>
        {hubName} is not included in your subscription
      </div>
      <div style={{ fontSize: 13 }}>
        Contact your account manager to enable this hub.
      </div>
    </div>
  );
}

export default function ClientDetail() {
  const { slug } = useParams();
  const [meta,     setMeta]     = useState({});
  const [features, setFeatures] = useState(null); // null = loading

  const { user } = useAuth();
  const asClient = new URLSearchParams(window.location.search).get('as') === 'client' || sessionStorage.getItem('stg_as_client') === '1';
  const isAdmin  = user?.role === 'admin' && !asClient;

  useEffect(() => {
    authFetch(`/api/clients/${slug}/meta`).then(r=>r.json()).then(d=>setMeta(d||{})).catch(()=>{});
  }, [slug]);

  useEffect(() => {
    authFetch(`/api/clients/${slug}/features`)
      .then(r => r.json())
      .then(d => setFeatures(d?.features || {}))
      .catch(() => setFeatures({}));
  }, [slug]);

  // Admin always sees all hubs; clients see only enabled ones
  const hubOk = (hubKey) => isAdmin || (features !== null && features[hubKey]);

  // First enabled hub for the index redirect (client view)
  const firstHub = () => {
    if (isAdmin) return 'cyber-security';
    if (!features) return 'cyber-security';
    if (features.hub_cyber)        return 'cyber-security';
    if (features.hub_device)       return 'devices';
    if (features.hub_digital)      return 'domains';
    if (features.hub_compliance)   return 'compliance';
    if (features.hub_productivity) return 'productivity';
    if (features.hub_assets)       return 'assets';
    return 'reports';
  };

  const displayName = meta.client_name || slug.replace(/_/g, ' ');

  return (
    <div>
      <div className="page-header">
        {isAdmin
          ? <Link to="/clients" className="back-link">← Clients</Link>
          : <Link to={`/clients/${slug}`} className="back-link">← Dashboard</Link>}
        <div className="page-title" style={{marginTop:8}}>{displayName}</div>
      </div>

      <Routes>
        <Route index element={<Navigate to={firstHub()} replace />} />
        <Route path="cyber-security"  element={hubOk('hub_cyber')        ? <CyberSecurityHubWrapper slug={slug} />                           : <HubDisabled hubName="Cyber Security Hub" />} />
        <Route path="domains"         element={hubOk('hub_digital')      ? <DigitalHubWrapper slug={slug} isAdmin={isAdmin} />               : <HubDisabled hubName="Digital Hub" />} />
        <Route path="devices"         element={hubOk('hub_device')       ? <DeviceHub slug={slug} isAdmin={isAdmin} />                       : <HubDisabled hubName="Device Hub" />} />
        <Route path="assets"          element={hubOk('hub_assets')       ? <AssetsHub slug={slug} isAdmin={isAdmin} />                       : <HubDisabled hubName="Assets Hub" />} />
        <Route path="compliance"      element={hubOk('hub_compliance')   ? <ComplianceHub slug={slug} meta={meta} isAdmin={isAdmin} />       : <HubDisabled hubName="Compliance Hub" />} />
        <Route path="productivity"    element={hubOk('hub_productivity') ? <ProductivityHubWrapper slug={slug} />                           : <HubDisabled hubName="Productivity Hub" />} />
        <Route path="tickets"         element={<TicketsHub  slug={slug} user={user} />} />
        <Route path="reports"         element={<ReportsHub  slug={slug} meta={meta} isAdmin={isAdmin} />} />
        <Route path="schedules"       element={hubOk('hub_schedules') ? <SchedulesHubWrapper slug={slug} /> : <HubDisabled hubName="Schedules" />} />
      </Routes>
    </div>
  );
}